import { useState, useMemo, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, Plus, Search, X, Loader2, Send, Eye, Download, Trash2, ArrowLeft,
  AlertTriangle, CheckCircle2, Ban, Pencil, Copy, Percent, ChevronRight, ChevronDown,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

interface Item {
  produto_id: number | null
  codigo: string
  descricao: string
  ncm: string
  cfop: string
  unidade: string
  quantidade: number
  valor_unitario: number
  valor_desconto: number
  origem: string
  cst: string | null
  csosn: string | null
  cst_pis: string
  cst_cofins: string
  ean?: string | null
  cest?: string | null
  // Tributação por item (o banco já guardava tudo isso; faltava editar na tela)
  aliquota_icms?: number | string | null
  reducao_bc_percent?: number | string | null
  modalidade_bc_st?: string | null
  mva_st?: number | string | null
  reducao_bc_st_percent?: number | string | null
  base_calculo_st?: number | string | null
  aliquota_icms_st?: number | string | null
  valor_icms_st?: number | string | null
  base_calculo_st_retido?: number | string | null
  valor_icms_st_retido?: number | string | null
  aliquota_pis?: number | string | null
  aliquota_cofins?: number | string | null
  ipi_cst?: string | null
  ipi_aliquota?: number | string | null
  ipi_valor?: number | string | null
  ipi_codigo_enquadramento?: string | null
  valor_total_tributos?: number | string | null
  informacoes_adicionais?: string | null
  // Linhagem de kit (migration 0077). A nota guarda a LINHA DO KIT
  // (eh_kit_pai) e as linhas dos COMPONENTES (kit_pai_produto_id). Só uma das
  // duas vai pro XML, conforme o perfil da filial: industria manda o kit, loja
  // manda os componentes. A tela mostra o kit e esconde as peças até clicar na seta.
  // IBS/CBS — Reforma Tributária. Vazio = grupo UB não emitido.
  cst_ibs_cbs?: string | null
  c_class_trib?: string | null
  ibs_base?: number | string | null
  p_ibs_uf?: number | string | null
  p_ibs_mun?: number | string | null
  p_cbs?: number | string | null
  eh_kit_pai?: boolean
  kit_pai_produto_id?: number | null
  kit_pai_quantidade?: number | string | null
  // Campos que o operador editou na mão. A regra de imposto reaplica a cada
  // save e pula estes — é o que faz a correção sobreviver ao próximo PUT.
  campos_manuais?: string[]
}

/** Espelha CAMPOS_REGRA_IMPOSTO (fiscal/models.py). Se divergir, a tela aplica
 *  um conjunto e o backend grava outro no save — mantenha as duas juntas. */
const CAMPOS_REGRA = [
  'origem', 'cst', 'csosn', 'cst_pis', 'cst_cofins',
  'aliquota_icms', 'aliquota_pis', 'aliquota_cofins', 'reducao_bc_percent',
  'modalidade_bc_st', 'mva_st', 'reducao_bc_st_percent', 'aliquota_icms_st',
  'ipi_cst', 'ipi_aliquota', 'ipi_codigo_enquadramento',
  'p_fcp', 'p_fcp_st', 'codigo_beneficio',
  'cst_ibs_cbs', 'c_class_trib', 'p_ibs_uf', 'p_ibs_mun', 'p_cbs',
  'informacoes_adicionais',
] as readonly string[]

/** Sobrepõe a tributação da regra, respeitando o que foi editado na mão.
 *  Nulo na regra = herda do produto. Zero = aplica zero, de propósito. */
const aplicarRegra = (it: Item, regra: any): Item => {
  if (!regra) return it
  const manuais = new Set(it.campos_manuais || [])
  const out: any = { ...it }
  for (const c of CAMPOS_REGRA) {
    if (manuais.has(c)) continue
    const v = regra[c]
    if (v === null || v === undefined) continue
    out[c] = v
  }
  return out as Item
}

/** Registra que estes campos foram decididos pelo operador. */
const marcarManual = (it: Item, campos: string[]): Item => {
  const atuais = new Set(it.campos_manuais || [])
  campos.filter(c => CAMPOS_REGRA.includes(c)).forEach(c => atuais.add(c))
  return { ...it, campos_manuais: [...atuais] }
}

const ORIGENS = [
  ['0', '0 - Nacional'], ['1', '1 - Estrangeira (importação direta)'],
  ['2', '2 - Estrangeira (mercado interno)'], ['3', '3 - Nacional, +40% importação'],
  ['4', '4 - Nacional (processos básicos)'], ['5', '5 - Nacional, até 40% importação'],
  ['6', '6 - Estrangeira (importação direta, s/ similar)'],
  ['7', '7 - Estrangeira (mercado interno, s/ similar)'], ['8', '8 - Nacional, +70% importação'],
] as const

/** Campo numérico da tributação.
 *
 * DEFINIDO AQUI FORA de propósito: quando ficava dentro do render do modal, o
 * React tratava cada teclada como um componente novo, remontava o input e o foco
 * saía — só dava pra digitar 1 caractere por clique.
 */
function CampoTrib({ label, campo, item, onChange, disabled, placeholder = '', dica = '' }: {
  label: string; campo: string; item: any; onChange: (c: string, v: any) => void
  disabled?: boolean; placeholder?: string; dica?: string
}) {
  return (
    <div>
      <label className="label" title={dica}>{label}</label>
      <input type="number" step="0.0001" className="input" placeholder={placeholder}
        disabled={disabled} value={item[campo] ?? ''}
        onChange={e => onChange(campo, e.target.value === '' ? null : e.target.value)} />
    </div>
  )
}

const MODALIDADES_BC_ST = [
  ['0', '0 - Preço tabelado'], ['1', '1 - Lista negativa'], ['2', '2 - Lista positiva'],
  ['3', '3 - Lista neutra'], ['4', '4 - Margem Valor Agregado (MVA)'], ['5', '5 - Pauta'],
] as const

/** Modal de tributação do item.
 *
 * COMPONENTE PRÓPRIO, no nível do módulo, com RASCUNHO INTERNO — de propósito.
 * Quando este modal ficava escrito dentro do render da página, cada tecla
 * alterava `itens`, a página re-renderizava e o modal era reconstruído: o input
 * perdia o foco e só dava pra digitar um caractere por clique.
 * Agora, digitar só mexe no estado daqui; os valores vão pro item ao clicar em
 * "Pronto".
 */
function ModalTributacao({ item, usaCsosn, editavel, kitVaiInteiro, onFechar, onSalvar }: {
  item: Item; usaCsosn: boolean; editavel: boolean; kitVaiInteiro: boolean
  onFechar: () => void; onSalvar: (it: Item) => void
}) {
  const [d, setD] = useState<Item>(item)
  // Trocou de item (abriu outro) → recarrega o rascunho.
  useEffect(() => { setD(item) }, [item])
  const setC = (campo: string, v: any) => setD(prev => ({ ...prev, [campo]: v }))
  const num = (campo: string) => (d as any)[campo] ?? ''

  const inputNum = (label: string, campo: string, placeholder = '', dica = '') => (
    <div key={campo}>
      <label className="label" title={dica}>{label}</label>
      <input type="number" step="0.0001" className="input" placeholder={placeholder}
        disabled={!editavel} value={num(campo)}
        onChange={e => setC(campo, e.target.value === '' ? null : e.target.value)} />
    </div>
  )
  const inputTexto = (label: string, campo: string, placeholder = '', ajuda?: string) => (
    <div key={campo}>
      <label className="label">{label}</label>
      <input className="input" disabled={!editavel} placeholder={placeholder}
        value={(d as any)[campo] || ''} onChange={e => setC(campo, e.target.value)} />
      {ajuda && <p className="text-[10px] text-ink-400 mt-0.5">{ajuda}</p>}
    </div>
  )

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b shrink-0 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Tributação do item</h3>
            <p className="text-xs text-ink-500">{d.codigo} · {d.descricao}</p>
          </div>
          <button onClick={onFechar} className="text-ink-400 hover:text-ink-700"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Qual lado do kit vale nesta nota — o outro fica guardado sem sair no XML */}
          {(item.eh_kit_pai || item.kit_pai_produto_id) && (
            <div className={`text-xs rounded-lg px-3 py-2 border ${
              (item.eh_kit_pai ? kitVaiInteiro : !kitVaiInteiro)
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              {item.eh_kit_pai
                ? (kitVaiInteiro
                  ? 'Esta filial é Indústria: é ESTA tributação que vai pra nota. Os componentes ficam de fora.'
                  : 'Esta filial é Loja: a nota sai pelos componentes, então o que você preencher aqui NÃO vai pro XML. Fica guardado pra quando a nota for emitida por uma filial Indústria.')
                : (kitVaiInteiro
                  ? 'Esta filial é Indústria: a nota sai pela bike montada, então o que você preencher aqui NÃO vai pro XML.'
                  : 'Esta filial é Loja: é ESTA tributação, de cada componente, que vai pra nota.')}
            </div>
          )}

          {/* ICMS */}
          <div>
            <div className="text-sm font-semibold text-ink-800 mb-2">ICMS</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className="label">Origem da mercadoria</label>
                <select className="input" disabled={!editavel} value={d.origem || '0'}
                  onChange={e => setC('origem', e.target.value)}>
                  {ORIGENS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {usaCsosn
                ? inputTexto('CSOSN', 'csosn', '102', 'Filial no Simples Nacional')
                : inputTexto('CST ICMS', 'cst', '00', 'Filial no Regime Normal')}
              {inputNum('% ICMS', 'aliquota_icms', '18')}
              {inputNum('% Redução de BC', 'reducao_bc_percent', '0',
                'Redução da base de cálculo do ICMS (CST 20 / CSOSN 900)')}
            </div>
          </div>

          {/* ICMS-ST */}
          <div className="border-t pt-3">
            <div className="text-sm font-semibold text-ink-800 mb-2">
              ICMS Substituição Tributária
              <span className="font-normal text-xs text-ink-500 ml-2">
                (deixe zerado quando não houver ST)
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className="label">Modalidade da BC ST</label>
                <select className="input" disabled={!editavel} value={d.modalidade_bc_st || '4'}
                  onChange={e => setC('modalidade_bc_st', e.target.value)}>
                  {MODALIDADES_BC_ST.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {inputNum('% MVA', 'mva_st', '0')}
              {inputNum('% Redução BC ST', 'reducao_bc_st_percent', '0')}
              {inputNum('Base ST (R$)', 'base_calculo_st', '0,00')}
              {inputNum('% ICMS ST', 'aliquota_icms_st', '0')}
              {inputNum('Valor ST (R$)', 'valor_icms_st', '0,00')}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              {inputNum('Base ST retido (R$)', 'base_calculo_st_retido', '0,00',
                'CST 60 / CSOSN 500: ST já cobrado anteriormente')}
              {inputNum('Valor ST retido (R$)', 'valor_icms_st_retido', '0,00')}
            </div>
          </div>

          {/* FCP — Fundo de Combate à Pobreza */}
          <div>
            <h4 className="text-xs font-semibold text-ink-700 mb-2">
              FCP <span className="font-normal text-ink-500">
                (adicional estadual — deixe zerado quando o estado não cobrar)
              </span>
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {inputNum('% FCP', 'p_fcp', '0')}
              {inputNum('% FCP ST', 'p_fcp_st', '0')}
              {inputTexto('Cód. benefício fiscal', 'codigo_beneficio', '—')}
            </div>
            <p className="text-[11px] text-ink-500 mt-1">
              Em branco, o sistema usa o percentual cadastrado no produto para a UF de
              destino. O código de benefício tem 8 ou 10 caracteres, ou o texto
              SEM CBENEF, e só vale em CST 20, 30, 40, 41, 50, 51, 70 e 90.
            </p>
          </div>

          {/* PIS / COFINS */}
          <div className="border-t pt-3">
            <div className="text-sm font-semibold text-ink-800 mb-2">PIS / COFINS</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {inputTexto('CST PIS', 'cst_pis', '49')}
              {inputNum('% PIS', 'aliquota_pis', '0')}
              {inputNum('Base PIS (R$)', 'base_pis', 'valor do item')}
              {inputTexto('CST COFINS', 'cst_cofins', '49')}
              {inputNum('% COFINS', 'aliquota_cofins', '0')}
              {inputNum('Base COFINS (R$)', 'base_cofins', 'valor do item')}
            </div>
          </div>

          {/* IPI */}
          <div className="border-t pt-3">
            <div className="text-sm font-semibold text-ink-800 mb-2">
              IPI
              <span className="font-normal text-xs text-ink-500 ml-2">
                (só quando a operação tem IPI — deixe o CST vazio se não tiver)
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {inputTexto('CST IPI', 'ipi_cst', '99')}
              {inputNum('% IPI', 'ipi_aliquota', '0')}
              {inputNum('Valor IPI (R$)', 'ipi_valor', '0,00')}
              {inputTexto('Cód. enquadramento', 'ipi_codigo_enquadramento', '999')}
            </div>
          </div>

          {/* IBS / CBS — Reforma Tributária */}
          <div>
            <h4 className="text-xs font-semibold text-ink-700 mb-2">
              IBS / CBS <span className="font-normal text-ink-500">
                (Reforma Tributária — deixe vazio se o contador ainda não definiu)
              </span>
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {inputTexto('CST IBS/CBS', 'cst_ibs_cbs', '000')}
              {inputTexto('cClassTrib', 'c_class_trib', '000001')}
              {inputNum('Base IBS/CBS (R$)', 'ibs_base', 'valor do item')}
              {inputNum('% IBS UF', 'p_ibs_uf', '0,1000')}
              {inputNum('% IBS Município', 'p_ibs_mun', '0,0000')}
              {inputNum('% CBS', 'p_cbs', '0,9000')}
            </div>
            <p className="text-[11px] text-ink-500 mt-1">
              Os três primeiros dígitos do cClassTrib repetem o CST. Em branco, o
              sistema usa o enquadramento do produto e as alíquotas de 2026.
            </p>
          </div>

          {/* Extras */}
          <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {inputNum('Trib. aproximados (R$)', 'valor_total_tributos', '0,00',
              'Lei da Transparência — valor aproximado dos tributos deste item')}
            <div className="md:col-span-2">
              <label className="label">Informação adicional do item</label>
              <input className="input" disabled={!editavel}
                value={d.informacoes_adicionais || ''}
                onChange={e => setC('informacoes_adicionais', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t shrink-0 flex justify-between items-center gap-2">
          <span className="text-[11px] text-ink-500">
            Em branco = o sistema usa o padrão. Confira com o contador.
          </span>
          <div className="flex gap-2">
            <button onClick={onFechar} className="btn-secondary">Cancelar</button>
            <button onClick={() => { onSalvar(d); onFechar() }} className="btn-primary">Pronto</button>
          </div>
        </div>
      </div>
    </div>
  )
}

const FORM_VAZIO = {
  filial_id: '' as number | '',
  regra_imposto_id: null as number | null,
  // venda de origem (importada pelo nº). Precisa existir aqui, senão o
  // abrirNota — que repovoa o form pelas chaves de FORM_VAZIO — perde o vínculo.
  pedido_id: null as number | null,
  tipo_operacao: '1',
  finalidade: '1',
  chave_nfe_referenciada: '',
  nf_entrada_id: null as number | null,
  filial_destino_id: null as number | null,
  nota_origem_id: null as number | null,
  natureza_operacao: '',
  // Qual operação do cadastro de CFOP originou a nota (guarda o código do CFOP
  // escolhido no seletor "O que é esta nota?"). Vai pro backend e volta ao reabrir.
  natureza_operacao_codigo: '',
  cfop_principal: '',
  consumidor_final: '',
  presenca_comprador: '9',
  modalidade_frete: '9',
  destinatario_id: null as number | null,
  destinatario_tipo: 'cliente',
  destinatario_razao_social: '',
  destinatario_cnpj_cpf: '',
  destinatario_ie: '',
  destinatario_indicador_ie: '9',
  destinatario_endereco: '',
  destinatario_numero: '',
  destinatario_complemento: '',
  destinatario_bairro: '',
  destinatario_municipio: '',
  destinatario_codigo_ibge: '',
  destinatario_uf: '',
  destinatario_cep: '',
  destinatario_telefone: '',
  destinatario_email: '',
  gera_estoque: false,
  gera_financeiro: false,
  forma_pagamento: '',
  financeiro_parcelas: 1,
  financeiro_primeiro_vencimento: '' as string,
  valor_frete: '0',
  valor_seguro: '0',
  valor_outras_despesas: '0',
  informacoes_adicionais: '',
  informacoes_fisco: '',
  transp_cnpj_cpf: '', transp_nome: '', transp_ie: '', transp_endereco: '',
  transp_municipio: '', transp_uf: '', transp_placa: '', transp_uf_placa: '', transp_rntc: '',
  vol_quantidade: '' as string, vol_especie: '', vol_marca: '', vol_numeracao: '',
  vol_peso_liquido: '' as string, vol_peso_bruto: '' as string,
}

const STATUS_CFG: Record<string, { label: string; cls: string }> = {
  rascunho: { label: 'Rascunho', cls: 'bg-ink-100 text-ink-700' },
  emitindo: { label: 'Emitindo...', cls: 'bg-blue-50 text-blue-700' },
  pendente_consulta: { label: 'Aguardando consulta', cls: 'bg-amber-100 text-amber-800' },
  autorizada: { label: 'Autorizada', cls: 'bg-emerald-50 text-emerald-700' },
  rejeitada: { label: 'Rejeitada', cls: 'bg-rose-50 text-rose-700' },
  cancelada: { label: 'Cancelada', cls: 'bg-ink-100 text-ink-500 line-through' },
}

export default function NotaAvulsaPage() {
  const qc = useQueryClient()
  const { id: idUrl } = useParams()
  const navigate = useNavigate()
  // Guarda qual nota já foi carregada pra esta URL: sem isso, cada re-render
  // dispararia abrirNota de novo e descartaria o que o operador digitou.
  const abertaRef = useRef<number | null>(null)
  const [modo, setModo] = useState<'lista' | 'form'>('lista')
  const [notaId, setNotaId] = useState<number | null>(null)
  const [form, setForm] = useState({ ...FORM_VAZIO })
  const [itens, setItens] = useState<Item[]>([])
  const [tribIdx, setTribIdx] = useState<number | null>(null)   // item aberto no editor de tributação
  // Produtos kit com os componentes visíveis na grade. Fechado por padrão: o
  // operador vendeu 1 bicicleta, não 26 peças.
  const [kitsAbertos, setKitsAbertos] = useState<Set<number>>(new Set())
  const [buscaProd, setBuscaProd] = useState('')
  const [abrirProd, setAbrirProd] = useState(false)
  const [buscaPessoa, setBuscaPessoa] = useState('')
  const [preview, setPreview] = useState<any>(null)
  const [evento, setEvento] = useState<null | 'cancelar' | 'cce'>(null)
  const [textoEvento, setTextoEvento] = useState('')
  const [senhaEvento, setSenhaEvento] = useState('')
  const [importarNF, setImportarNF] = useState(false)
  // Período padrão: mês passado — é o que o contador pede, e evita puxar o mês
  // corrente pela metade.
  const [exportar, setExportar] = useState(false)
  const [expDe, setExpDe] = useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1)
    return d.toISOString().slice(0, 10)
  })
  const [expAte, setExpAte] = useState(() => {
    const d = new Date(); d.setDate(0)   // último dia do mês anterior
    return d.toISOString().slice(0, 10)
  })
  const [enviarZap, setEnviarZap] = useState(false)
  const [foneZap, setFoneZap] = useState('')
  const [importarXml, setImportarXml] = useState(false)
  const [xmlTexto, setXmlTexto] = useState('')
  const [buscaNF, setBuscaNF] = useState('')

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  // ===== importar de uma venda =====
  const [codVenda, setCodVenda] = useState('')
  const [importando, setImportando] = useState(false)
  const [impAvisos, setImpAvisos] = useState<string[]>([])

  /** Puxa destinatário + itens pelo nº do pedido. O backend NÃO grava nada:
   *  o operador ainda revisa, escolhe natureza/CFOP e salva como sempre. */
  const importarVenda = async () => {
    const num = Number(String(codVenda).replace(/\D/g, ''))
    if (!num) { toast.error('Informe o número da venda'); return }
    if (itens.length && !window.confirm('Isto substitui os itens já lançados. Continuar?')) return
    setImportando(true)
    try {
      const { data } = await api.post('/nota-avulsa/importar-pedido', { numero: num })
      const { itens: linhas, ...campos } = data.nota
      // null vira '' — os inputs da tela são controlados e quebram com null.
      const limpos = Object.fromEntries(
        Object.entries(campos).map(([k, v]) => [k, v === null ? '' : v]))
      setForm(f => ({ ...f, ...limpos, pedido_id: data.pedido.id } as any))
      setItensComRegra((linhas || []).map((i: any) => ({
        ...i,
        quantidade: Number(i.quantidade),
        valor_unitario: Number(i.valor_unitario),
        valor_desconto: Number(i.valor_desconto || 0),
        aliquota_icms: i.aliquota_icms != null ? Number(i.aliquota_icms) : null,
        kit_pai_quantidade: i.kit_pai_quantidade != null ? Number(i.kit_pai_quantidade) : null,
      })))
      setKitsAbertos(new Set())   // kit entra recolhido, como no addProduto
      setImpAvisos(data.avisos || [])
      toast.success(`Venda ${data.pedido.numero} importada — ${(linhas || []).length} linha(s)`)
    } catch (e: any) {
      setImpAvisos([])
      toast.error(e?.response?.data?.detail || 'Erro ao importar a venda')
    } finally {
      setImportando(false)
    }
  }

  // ===== dados auxiliares =====
  const { data: notas = [], isLoading } = useQuery<any[]>({
    queryKey: ['notas-avulsas'],
    queryFn: async () => (await api.get('/nota-avulsa')).data.notas,
  })
  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-nota-avulsa'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const { data: cfops = [] } = useQuery<any[]>({
    queryKey: ['cfops-nota-avulsa'],
    queryFn: async () => (await api.get('/fiscal/cfops')).data,
  })
  // Mesma lista do PDV/Novo Pedido (nomes e ordem configurados por empresa).
  // O código da forma é o que o backend mapeia pro tPag da NF-e.
  const { data: formasConfig = [] } = useQuery<any[]>({
    queryKey: ['config-formas'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  const formasPagamento = useMemo(
    () => (formasConfig as any[])
      .filter(f => f.ativo !== false)
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)),
    [formasConfig],
  )
  const { data: pessoas = [] } = useQuery<any[]>({
    queryKey: ['pessoas-nota-avulsa', buscaPessoa, form.destinatario_tipo],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { q: buscaPessoa, tipo: form.destinatario_tipo, limit: 15 },
    })).data,
    enabled: buscaPessoa.trim().length >= 2,
  })
  const { data: produtos = [] } = useQuery<any[]>({
    queryKey: ['produtos-nota-avulsa', buscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', {
      params: { q: buscaProd.trim() || undefined, limit: 20 },
    })).data,
    enabled: abrirProd && buscaProd.trim().length > 0,
  })

  const ehEntrada = form.tipo_operacao === '0'
  // CFOP muda de família conforme o sentido: entrada usa 1xxx/2xxx, saída 5xxx/6xxx.
  const cfopsFiltrados = useMemo(
    () => cfops.filter((c: any) => (ehEntrada ? ['1', '2'] : ['5', '6']).includes((c.codigo || '')[0])),
    [cfops, ehEntrada],
  )
  const exigeRef = ['2', '4'].includes(form.finalidade)
  // A linha do kit NÃO entra na soma: o valor dela é o mesmo dos componentes
  // somados, e contar os dois dobraria a nota (mesma regra do _gravar_itens).
  const totalItens = itens
    .filter(i => !i.eh_kit_pai)
    .reduce((s, i) => s + (i.quantidade * i.valor_unitario - (i.valor_desconto || 0)), 0)

  // Qual lado do kit vale nesta nota — decide o que a grade destaca e o aviso do modal.
  const perfilFilial = useMemo(() => {
    const f = (filiais as any[]).find(x => x.id === Number(form.filial_id))
    return (f?.perfil_operacao || 'loja') as 'loja' | 'industria'
  }, [filiais, form.filial_id])
  const kitVaiInteiro = perfilFilial === 'industria'
  const totalNota = totalItens + Number(form.valor_frete || 0) + Number(form.valor_seguro || 0)
    + Number(form.valor_outras_despesas || 0)

  const notaAtual = notas.find((n: any) => n.id === notaId)
  const notaAtualStatus = notaAtual?.status || 'rascunho'
  const editavel = !notaId || ['rascunho', 'rejeitada'].includes(notaAtualStatus)

  // ===== mutations =====
  const salvar = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        filial_id: form.filial_id || null,
        consumidor_final: form.consumidor_final || null,
        chave_nfe_referenciada: form.chave_nfe_referenciada || null,
        forma_pagamento: form.forma_pagamento || null,
        financeiro_primeiro_vencimento: form.financeiro_primeiro_vencimento || null,
        financeiro_parcelas: Number(form.financeiro_parcelas) || 1,
        vol_quantidade: form.vol_quantidade ? Number(form.vol_quantidade) : null,
        vol_peso_liquido: form.vol_peso_liquido || null,
        vol_peso_bruto: form.vol_peso_bruto || null,
        itens: itens.map(i => ({ ...i, valor_total: i.quantidade * i.valor_unitario - (i.valor_desconto || 0) })),
      }
      return notaId
        ? (await api.put(`/nota-avulsa/${notaId}`, payload)).data
        : (await api.post('/nota-avulsa', payload)).data
    },
    onSuccess: (d) => {
      setNotaId(d.id)
      // Rascunho novo ganha URL própria — dá pra recarregar/compartilhar.
      if (!idUrl) { abertaRef.current = d.id; navigate(`/fiscal/nota-avulsa/${d.id}`, { replace: true }) }
      toast.success('Nota salva')
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      qc.invalidateQueries({ queryKey: ['nota-avulsa-validar'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  const { data: validacao } = useQuery<any>({
    queryKey: ['nota-avulsa-validar', notaId, notaAtualStatus],
    queryFn: async () => (await api.get(`/nota-avulsa/${notaId}/validar`)).data,
    enabled: !!notaId && modo === 'form',
  })

  const verPreview = useMutation({
    mutationFn: async () => (await api.get(`/nota-avulsa/${notaId}/preview-xml`)).data,
    onSuccess: (d) => setPreview(d),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no preview'),
  })

  const emitir = useMutation({
    mutationFn: async () => (await api.post(`/nota-avulsa/${notaId}/emitir`)).data,
    onSuccess: (d) => {
      if (d.autorizada) {
        toast.success(`Nota ${d.numero} autorizada`, { icon: '✅' })
        if (d.efeitos?.pre_entrada?.startsWith?.('criada')) {
          toast.success('Pré-entrada criada na loja de destino — ela precisa conferir e confirmar.',
            { duration: 8000 })
        }
        if (d.efeitos?.erros?.length) {
          toast.error(`Nota OK, mas: ${d.efeitos.erros.join(' · ')}`, { duration: 9000 })
        }
      } else {
        toast.error(`SEFAZ rejeitou: ${d.cStat || ''} ${d.xMotivo || d.erro || ''}`, { duration: 12000 })
      }
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      irParaNota(d.nota_id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao emitir'),
  })

  const { data: eventos } = useQuery<any>({
    queryKey: ['nota-avulsa-eventos', notaId, notaAtualStatus],
    queryFn: async () => (await api.get(`/nota-avulsa/${notaId}/eventos`)).data,
    enabled: !!notaId && modo === 'form' && !!notaAtual?.nfe_emissao_id,
  })

  const cancelarNota = useMutation({
    mutationFn: async () => {
      // Mesmo gate das exclusões do sistema e do cancelamento de NF-e da venda:
      // exige a senha de um admin/gerente/financeiro. Valida primeiro (não apaga
      // nada) e só então cancela de verdade na SEFAZ.
      await api.post('/auth/autorizar-exclusao', { senha: senhaEvento })
      return (await api.post(`/nota-avulsa/${notaId}/cancelar`, { justificativa: textoEvento })).data
    },
    onSuccess: (d) => {
      if (d.sucesso) {
        toast.success('Nota cancelada na SEFAZ')
        setEvento(null); setTextoEvento(''); setSenhaEvento('')
      } else {
        toast.error(`SEFAZ recusou: ${d.cStat || ''} ${d.xMotivo || d.erro || ''}`, { duration: 10000 })
      }
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      qc.invalidateQueries({ queryKey: ['nota-avulsa-eventos'] })
    },
    onError: (e: any) => {
      // 403 aqui quase sempre é a senha de autorização, não o cancelamento em si.
      const msg = e?.response?.data?.detail
      toast.error(e?.response?.status === 403
        ? (msg || 'Senha de autorização incorreta.')
        : (msg || 'Erro ao cancelar'))
    },
  })

  const emitirCCe = useMutation({
    mutationFn: async () =>
      (await api.post(`/nota-avulsa/${notaId}/cc-e`, { texto: textoEvento })).data,
    onSuccess: (d) => {
      if (d.sucesso) {
        toast.success('Carta de correção registrada')
        setEvento(null); setTextoEvento(''); setSenhaEvento('')
      } else {
        toast.error(`SEFAZ recusou: ${d.cStat || ''} ${d.xMotivo || d.erro || ''}`, { duration: 10000 })
      }
      qc.invalidateQueries({ queryKey: ['nota-avulsa-eventos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro na carta de correção'),
  })

  const { data: remessas } = useQuery<any>({
    queryKey: ['remessas-abertas'],
    queryFn: async () => (await api.get('/nota-avulsa/remessas-abertas')).data,
    enabled: modo === 'lista',
  })

  const gerarRetorno = useMutation({
    mutationFn: async (id: number) => (await api.post(`/nota-avulsa/${id}/gerar-retorno`)).data,
    onSuccess: (d) => {
      toast.success('Retorno criado — confira as quantidades e emita')
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      qc.invalidateQueries({ queryKey: ['remessas-abertas'] })
      irParaNota(d.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar retorno'),
  })

  const { data: nfEntradas = [] } = useQuery<any[]>({
    queryKey: ['nf-entradas-devolucao', buscaNF],
    queryFn: async () => (await api.get('/nota-avulsa/nf-entradas', {
      params: { q: buscaNF.trim() || undefined },
    })).data,
    enabled: importarNF,
  })

  const importarDoXml = useMutation({
    mutationFn: async () => (await api.post('/nota-avulsa/importar-xml', { xml: xmlTexto })).data,
    onSuccess: (d) => {
      const i = d._importacao
      toast.success(i?.tipo === 'devolucao'
        ? `Devolução da NF ${i.nf_origem} criada (${i.itens} itens)`
        : `Cópia da NF ${i?.nf_origem} criada`)
      setImportarXml(false); setXmlTexto('')
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      irParaNota(d.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao ler o XML', { duration: 9000 }),
  })

  const importar = useMutation({
    mutationFn: async (nfId: number) =>
      (await api.post(`/nota-avulsa/importar-nf-entrada/${nfId}`)).data,
    onSuccess: (d) => {
      toast.success('Devolução criada a partir da NF de entrada')
      setImportarNF(false); setBuscaNF('')
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      abrirNota(d.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao importar'),
  })

  const consultar = useMutation({
    mutationFn: async () => (await api.post(`/nota-avulsa/${notaId}/consultar-sefaz`)).data,
    onSuccess: (d) => {
      if (d.status === 'autorizada') toast.success('A SEFAZ tinha autorizado — nota regularizada')
      else if (d.status === 'rascunho') toast.success('Não existia na SEFAZ — pode emitir de novo')
      else toast.error(`Consulta inconclusiva: ${d.cStat || ''} ${d.xMotivo || d.erro || ''}`, { duration: 10000 })
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      if (notaId) abrirNota(notaId)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao consultar'),
  })

  const enviarWhats = useMutation({
    mutationFn: async () =>
      (await api.post(`/nota-avulsa/${notaId}/enviar-whatsapp`, { telefone: foneZap })).data,
    onSuccess: (d) => {
      toast.success(d.xml
        ? 'DANFE e XML enviados pelo WhatsApp'
        : 'DANFE enviada. O XML não foi aceito pelo aparelho — mande o arquivo manualmente.')
      setEnviarZap(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao enviar', { duration: 9000 }),
  })

  const duplicar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/nota-avulsa/${id}/duplicar`)).data,
    onSuccess: (d) => {
      toast.success('Cópia criada como rascunho')
      qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
      irParaNota(d.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao duplicar'),
  })

  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/nota-avulsa/${id}`),
    onSuccess: () => { toast.success('Excluída'); qc.invalidateQueries({ queryKey: ['notas-avulsas'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  // ===== navegação =====
  const novaNota = () => {
    abertaRef.current = null
    if (idUrl) navigate('/fiscal/nota-avulsa')
    setNotaId(null); setForm({ ...FORM_VAZIO }); setItens([]); setPreview(null)
    setKitsAbertos(new Set()); setModo('form')
  }
  // Abre pela URL — o efeito acima é quem chama abrirNota de fato.
  const irParaNota = (id: number) => {
    if (Number(idUrl) === id) abrirNota(id)
    else navigate(`/fiscal/nota-avulsa/${id}`)
  }
  const abrirNota = async (id: number) => {
    const d = (await api.get(`/nota-avulsa/${id}`)).data
    setNotaId(d.id)
    setForm({
      ...FORM_VAZIO,
      ...Object.fromEntries(Object.keys(FORM_VAZIO).map(k => [k, d[k] ?? (FORM_VAZIO as any)[k]])),
      filial_id: d.filial_id ?? '',
      valor_frete: String(d.valor_frete ?? '0'),
      valor_seguro: String(d.valor_seguro ?? '0'),
      valor_outras_despesas: String(d.valor_outras_despesas ?? '0'),
    } as any)
    setItens((d.itens || []).map((i: any) => ({
      produto_id: i.produto_id, codigo: i.codigo, descricao: i.descricao, ncm: i.ncm || '',
      cfop: i.cfop || '', unidade: i.unidade || 'UN', quantidade: Number(i.quantidade),
      valor_unitario: Number(i.valor_unitario), valor_desconto: Number(i.valor_desconto || 0),
      origem: i.origem || '0', cst: i.cst, csosn: i.csosn,
      cst_pis: i.cst_pis || '49', cst_cofins: i.cst_cofins || '49',
      ean: i.ean, cest: i.cest,
      // Tributação — precisa voltar, senão salvar depois apagaria o que foi preenchido
      aliquota_icms: i.aliquota_icms, reducao_bc_percent: i.reducao_bc_percent,
      modalidade_bc_st: i.modalidade_bc_st, mva_st: i.mva_st,
      reducao_bc_st_percent: i.reducao_bc_st_percent, base_calculo_st: i.base_calculo_st,
      aliquota_icms_st: i.aliquota_icms_st, valor_icms_st: i.valor_icms_st,
      base_calculo_st_retido: i.base_calculo_st_retido,
      valor_icms_st_retido: i.valor_icms_st_retido,
      aliquota_pis: i.aliquota_pis, aliquota_cofins: i.aliquota_cofins,
      ipi_cst: i.ipi_cst, ipi_aliquota: i.ipi_aliquota, ipi_valor: i.ipi_valor,
      ipi_codigo_enquadramento: i.ipi_codigo_enquadramento,
      valor_total_tributos: i.valor_total_tributos,
      informacoes_adicionais: i.informacoes_adicionais,
      // Linhagem de kit — sem isto a seta some ao reabrir e o Salvar seguinte
      // desmontaria o kit (o backend rederiva os filhos a partir da linha do pai).
      cst_ibs_cbs: i.cst_ibs_cbs ?? null,
      c_class_trib: i.c_class_trib ?? null,
      ibs_base: i.ibs_base ?? null,
      p_ibs_uf: i.p_ibs_uf ?? null,
      p_ibs_mun: i.p_ibs_mun ?? null,
      p_cbs: i.p_cbs ?? null,
      eh_kit_pai: !!i.eh_kit_pai,
      kit_pai_produto_id: i.kit_pai_produto_id ?? null,
      kit_pai_quantidade: i.kit_pai_quantidade ?? null,
    })))
    setPreview(null); setModo('form')
  }

  // Filial emitente resolvida uma vez — UF (decide CFOP intra/inter) e CNPJ (exibição).
  const filialEmit = useMemo(
    () => (filiais as any[]).find((f: any) => f.id === Number(form.filial_id)) || null,
    [filiais, form.filial_id],
  )
  // UF do emitente — sai da filial escolhida. Sem filial definida, assume intra
  // e deixa o backend corrigir (ele normaliza o 1º dígito do CFOP pelo destino).
  const ufEmitente = (filialEmit?.uf || '').toUpperCase()
  const cnpjEmitente = (() => {
    const d = String(filialEmit?.cnpj || '').replace(/\D/g, '')
    return d.length === 14
      ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
      : (filialEmit?.cnpj || '')
  })()
  const intraestadual = !ufEmitente || !form.destinatario_uf
    || ufEmitente === (form.destinatario_uf || '').toUpperCase()

  // As operações vêm do CADASTRO DE CFOP (Fiscal → CFOP): todo CFOP com
  // natureza_padrao preenchida vira uma opção aqui. Agrupa pelo par intra/inter
  // pra não listar "Transferência" duas vezes — o sistema escolhe 5152 ou 6152
  // conforme a UF do destinatário.
  const operacoes = useMemo(() => {
    const comNatureza = cfops.filter((c: any) => c.ativo !== false && c.natureza_padrao)
    const vistos = new Set<string>()
    const lista: any[] = []
    for (const c of comNatureza) {
      if (vistos.has(c.codigo)) continue
      const par = comNatureza.find((x: any) => x.codigo === c.cfop_par)
      vistos.add(c.codigo)
      if (par) vistos.add(par.codigo)
      const entrada = (c.codigo || '')[0] === '1' || (c.codigo || '')[0] === '2'
      lista.push({
        chave: c.codigo,
        label: c.natureza_padrao,
        dica: c.observacao_operacao,
        cfop_intra: entrada
          ? ((c.codigo[0] === '1' ? c.codigo : par?.codigo) || c.codigo)
          : ((c.codigo[0] === '5' ? c.codigo : par?.codigo) || c.codigo),
        cfop_inter: entrada
          ? ((c.codigo[0] === '2' ? c.codigo : par?.codigo) || c.codigo)
          : ((c.codigo[0] === '6' ? c.codigo : par?.codigo) || c.codigo),
        natureza: c.natureza_padrao,
        tipo_operacao: entrada ? '0' : '1',
        finalidade: c.finalidade || '1',
        consumidor_final: c.consumidor_final || '',
        gera_estoque: !!c.gera_estoque,
        gera_financeiro: !!c.gera_financeiro,
        cst_sugerido: c.cst_sugerido || null,
      })
    }
    return lista.sort((a, b) => a.label.localeCompare(b.label))
  }, [cfops])

  // Mesma conta do backend, só pra dar retorno na hora — quem trava de verdade
  // é a validação do servidor.
  const docValido = (() => {
    const d = (form.destinatario_cnpj_cpf || '').replace(/\D/g, '')
    if (!d) return null
    if (d.length !== 11 && d.length !== 14) return false
    if (d === d[0].repeat(d.length)) return false
    if (d.length === 11) {
      for (const tam of [9, 10]) {
        let soma = 0
        for (let i = 0; i < tam; i++) soma += Number(d[i]) * (tam + 1 - i)
        let resto = (soma * 10) % 11
        if (resto === 10) resto = 0
        if (resto !== Number(d[tam])) return false
      }
      return true
    }
    const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    for (const tam of [12, 13]) {
      const p = pesos.slice(-tam)
      let soma = 0
      for (let i = 0; i < tam; i++) soma += Number(d[i]) * p[i]
      const resto = soma % 11
      const dv = resto < 2 ? 0 : 11 - resto
      if (dv !== Number(d[tam])) return false
    }
    return true
  })()

  /** Baixa/abre arquivo da API.
   *
   * Link <a href> direto NÃO funciona: a autenticação é por token no header, que
   * o navegador não envia numa navegação comum — a API respondia "Not authenticated".
   * Busca com o token, vira blob e abre/baixa daí.
   */
  const baixarArquivo = async (url: string, nome: string, abrir = false) => {
    try {
      const r = await api.get(url, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = blobUrl
      if (abrir) { a.target = '_blank'; a.rel = 'noreferrer' } else { a.download = nome }
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Não foi possível gerar o arquivo')
    }
  }

  const buscarCep = async () => {
    const c = (form.destinatario_cep || '').replace(/\D/g, '')
    if (c.length !== 8) { toast.error('CEP precisa ter 8 dígitos'); return }
    try {
      const { data: d } = await api.get(`/nota-avulsa/cep/${c}`)
      setForm(f => ({
        ...f,
        destinatario_endereco: d.endereco || f.destinatario_endereco,
        destinatario_bairro: d.bairro || f.destinatario_bairro,
        destinatario_municipio: d.municipio || f.destinatario_municipio,
        destinatario_uf: d.uf || f.destinatario_uf,
        destinatario_codigo_ibge: d.codigo_ibge || f.destinatario_codigo_ibge,
      }))
      toast.success('Endereço preenchido')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Não foi possível buscar o CEP')
    }
  }

  // CRT da filial emitente decide o que vale: CSOSN (Simples) ou CST (Regime Normal).
  const crtFilial = String(filiais.find((f: any) => f.id === form.filial_id)?.crt || '')
  const usaCsosn = crtFilial === '1' || crtFilial === '2'

  // ===== regra de imposto =====
  // Filtra por COMPATIBILIDADE no backend: regra com crt/uf vazios serve a
  // qualquer um, então a regra genérica (o caso comum) não some do seletor.
  const { data: regras = [], isFetching: regrasCarregando } = useQuery<any[]>({
    queryKey: ['regras-imposto', form.cfop_principal, crtFilial, form.destinatario_uf],
    enabled: !!form.cfop_principal,
    queryFn: async () => (await api.get('/regras-imposto', {
      params: {
        cfop: form.cfop_principal,
        crt: crtFilial || undefined,
        uf_destino: form.destinatario_uf || undefined,
      },
    })).data.regras,
  })
  const regraAtual = (regras as any[]).find(
    r => r.id === Number(form.regra_imposto_id)) || null

  /** setItens que já aplica a regra vigente. Usado por tudo que ACRESCENTA
   *  linha — item novo precisa nascer com a regra, senão a nota fica com
   *  tributação misturada entre o antes e o depois da escolha. É idempotente:
   *  reaplicar nas linhas antigas não muda nada e respeita campos_manuais. */
  const setItensComRegra: typeof setItens = (updater) =>
    setItens(prev => {
      const next = typeof updater === 'function' ? (updater as any)(prev) : updater
      return regraAtual ? next.map((it: Item) => aplicarRegra(it, regraAtual)) : next
    })

  const escolherRegra = (id: number | '') => {
    set('regra_imposto_id', id ? Number(id) : null)
    const r = (regras as any[]).find(x => x.id === Number(id))
    if (!r) return
    setItens(prev => prev.map(it => aplicarRegra(it, r)))
    toast.success(`Regra "${r.nome}" aplicada a ${itens.length} item(ns)`)
  }

  /** Reaplica do zero: destrava tudo que foi editado na mão. A saída de quem
   *  editou errado e não quer apagar e relançar o item. */
  const reaplicarRegra = () => {
    if (!regraAtual) return
    setItens(prev => prev.map(it => aplicarRegra({ ...it, campos_manuais: [] }, regraAtual)))
    toast.success('Regra reaplicada — edições manuais descartadas.')
  }

  // A regra é vinculada ao CFOP: trocou o CFOP e ela não serve mais, sai. Sem
  // isto a nota ficaria com regra de uma operação e itens de outra.
  useEffect(() => {
    if (!form.regra_imposto_id || !form.cfop_principal || regrasCarregando) return
    if ((regras as any[]).some(r => r.id === Number(form.regra_imposto_id))) return
    set('regra_imposto_id', null)
    toast('Regra de imposto removida — não vale para o CFOP escolhido.')
  }, [regras, regrasCarregando, form.cfop_principal])  // eslint-disable-line react-hooks/exhaustive-deps
  const naturezaAtual = operacoes.find(n => n.chave === form.natureza_operacao_codigo)
  const cfopSugerido = naturezaAtual
    ? (intraestadual ? naturezaAtual.cfop_intra : naturezaAtual.cfop_inter)
    : ''

  /** Adiciona produto à nota — se for kit, já traz a bike + os componentes.
   *
   * A expansão vem do BACKEND (POST /nota-avulsa/expandir-kit), que roda o mesmo
   * helper usado ao gravar. Reescrever o rateio aqui em TypeScript faria a nota
   * fechar na tela e não fechar no XML na primeira divergência de centavo.
   */
  const addProdutoComKit = async (p: any) => {
    if (p.tipo !== 'kit') { addProduto(p); return }
    try {
      const { data } = await api.post('/nota-avulsa/expandir-kit', {
        produto_id: p.id,
        quantidade: 1,
        valor_unitario: Number(p.preco_venda || 0),
        cfop: cfopSugerido || undefined,
      })
      const novos: Item[] = (data.itens || []).map((i: any) => ({
        produto_id: i.produto_id ?? null,
        codigo: i.codigo || '', descricao: i.descricao || '',
        ncm: i.ncm || '', cfop: i.cfop || '', unidade: i.unidade || 'UN',
        quantidade: Number(i.quantidade || 0),
        valor_unitario: Number(i.valor_unitario || 0),
        valor_desconto: Number(i.valor_desconto || 0),
        origem: i.origem || '0', cst: i.cst ?? null, csosn: i.csosn ?? null,
        cst_pis: i.cst_pis || '49', cst_cofins: i.cst_cofins || '49',
        ean: i.ean ?? null, cest: i.cest ?? null,
        aliquota_icms: i.aliquota_icms ?? null,
        cst_ibs_cbs: i.cst_ibs_cbs ?? null,
        c_class_trib: i.c_class_trib ?? null,
        ibs_base: i.ibs_base ?? null,
        p_ibs_uf: i.p_ibs_uf ?? null,
        p_ibs_mun: i.p_ibs_mun ?? null,
        p_cbs: i.p_cbs ?? null,
        eh_kit_pai: !!i.eh_kit_pai,
        kit_pai_produto_id: i.kit_pai_produto_id ?? null,
        kit_pai_quantidade: i.kit_pai_quantidade ?? null,
      }))
      setItens(prev => [...prev, ...novos])
      // Kit entra recolhido: o operador vendeu 1 bicicleta, não 26 peças.
      setBuscaProd(''); setAbrirProd(false)
      toast.success(`${p.descricao}: ${novos.length - 1} componente(s) — clique na seta pra ver`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao expandir o kit')
    }
  }
  const cfopCadastrado = !cfopSugerido || cfops.some((c: any) => c.codigo === cfopSugerido)

  const aplicarNatureza = (chave: string) => {
    const n = operacoes.find(x => x.chave === chave)
    if (!n) { set('natureza_operacao_codigo', ''); return }
    const cfop = intraestadual ? n.cfop_intra : n.cfop_inter
    setForm(f => ({
      ...f,
      natureza_operacao_codigo: n.chave,
      natureza_operacao: n.natureza || f.natureza_operacao,
      tipo_operacao: n.tipo_operacao,
      finalidade: n.finalidade,
      cfop_principal: cfop || f.cfop_principal,
      consumidor_final: n.consumidor_final,
      presenca_comprador: '9',
      gera_estoque: n.gera_estoque,
      gera_financeiro: n.gera_financeiro,
    }))
    // Aplica CFOP e, se o cadastro tiver, o CST sugerido — é pra isso que o
    // campo cst_sugerido existe no cadastro de CFOP (nunca tinha sido usado).
    setItens(prev => prev.map(i => ({
      ...i,
      ...(cfop ? { cfop } : {}),
      ...(n.cst_sugerido ? { cst: n.cst_sugerido } : {}),
    })))
  }

  // Trocar a UF do destinatário muda o CFOP (5152 ↔ 6152). Reaplica sozinho pra
  // não sair nota interestadual com CFOP de dentro do estado.
  useEffect(() => {
    if (!naturezaAtual || !cfopSugerido || !editavel) return
    if (form.cfop_principal === cfopSugerido) return
    setForm(f => ({ ...f, cfop_principal: cfopSugerido }))
    setItens(prev => prev.map(i => ({ ...i, cfop: cfopSugerido })))
  }, [cfopSugerido])  // eslint-disable-line react-hooks/exhaustive-deps

  // A URL manda: /fiscal/nota-avulsa/123 abre a nota 123; sem id, mostra a lista.
  useEffect(() => {
    const id = idUrl ? Number(idUrl) : null
    if (id && !Number.isNaN(id)) {
      if (abertaRef.current !== id) {
        abertaRef.current = id
        abrirNota(id)
      }
    } else if (abertaRef.current !== null) {
      // Voltou pra /fiscal/nota-avulsa (ex.: botão voltar do navegador)
      abertaRef.current = null
      setModo('lista')
    }
  }, [idUrl])  // eslint-disable-line react-hooks/exhaustive-deps

  const addProduto = (p: any) => {
    setItensComRegra(prev => [...prev, {
      produto_id: p.id, codigo: p.codigo || '', descricao: p.descricao || '',
      ncm: p.ncm || '', cfop: form.cfop_principal || p.cfop || '', unidade: p.unidade || 'UN',
      quantidade: 1, valor_unitario: Number(p.preco_venda || 0), valor_desconto: 0,
      origem: p.origem || '0', cst: p.cst || null, csosn: p.csosn || null,
      cst_pis: p.cst_pis || '49', cst_cofins: p.cst_cofins || '49',
      ean: p.ean, cest: p.cest,
    }])
    setBuscaProd(''); setAbrirProd(false)
  }
  const addLivre = () => setItensComRegra(prev => [...prev, {
    produto_id: null, codigo: '', descricao: '', ncm: '', cfop: form.cfop_principal || '',
    unidade: 'UN', quantidade: 1, valor_unitario: 0, valor_desconto: 0, origem: '0',
    cst: null, csosn: null, cst_pis: '49', cst_cofins: '49',
  }])
  const setItem = (idx: number, campo: string, valor: any) =>
    setItens(prev => prev.map((it, i) => (
      i === idx ? marcarManual({ ...it, [campo]: valor }, [campo]) : it)))

    /** Mexeu na quantidade ou no valor da linha do kit → refaz as peças.
   *
   * O backend rederiva os componentes a partir da linha do kit a cada PUT, então
   * isto é só pra tela não ficar mostrando número velho até o próximo reload.
   * Mesma matemática do _expandir_kit_em_itens_nf: rateio proporcional, e o
   * ÚLTIMO componente absorve o centavo da divisão.
   */
  const setItemKitPai = (idx: number, campo: 'quantidade' | 'valor_unitario', valor: number) => {
    setItens(prev => {
      const pai = prev[idx]
      if (!pai?.produto_id) return prev
      const novoPai = { ...pai, [campo]: valor }
      const qtdNova = Number(campo === 'quantidade' ? valor : pai.quantidade) || 0
      const qtdAntiga = Number(pai.quantidade) || 0
      const baseNova = qtdNova * (Number(campo === 'valor_unitario' ? valor : pai.valor_unitario) || 0)

      const idxFilhos = prev
        .map((it, i) => (it.kit_pai_produto_id === pai.produto_id ? i : -1))
        .filter(i => i >= 0)
      if (!idxFilhos.length || qtdAntiga <= 0) {
        return prev.map((it, i) => (i === idx ? novoPai : it))
      }
      // Peso de cada componente = quantidade por kit (qtd atual ÷ nº de kits atual).
      const pesos = idxFilhos.map(i => Number(prev[i].quantidade) / qtdAntiga)
      const somaPesos = pesos.reduce((a, b) => a + b, 0) || 1

      let acumulado = 0
      const novos = [...prev]
      novos[idx] = novoPai
      idxFilhos.forEach((iFilho, n) => {
        const qtd = Number((pesos[n] * qtdNova).toFixed(4))
        const bruto = n < idxFilhos.length - 1
          ? Number(((baseNova * pesos[n]) / somaPesos).toFixed(2))
          : Number((baseNova - acumulado).toFixed(2))
        if (n < idxFilhos.length - 1) acumulado += bruto
        novos[iFilho] = {
          ...novos[iFilho],
          quantidade: qtd,
          valor_unitario: qtd > 0 ? Number((bruto / qtd).toFixed(6)) : 0,
          kit_pai_quantidade: qtdNova,
        }
      })
      return novos
    })
  }

  // ============================================================
  // LISTA
  // ============================================================
  if (modo === 'lista') {
    return (
      <div className="p-4 lg:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText size={22} className="text-brand-600" /> Nota Fiscal Avulsa
            </h1>
            <p className="text-xs text-ink-500">
              NF-e sem venda por trás: transferência, remessa p/ conserto, devolução, ativo imobilizado
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setImportarNF(true)} className="btn-secondary">
              <Download size={15} /> Devolver compra
            </button>
            <button onClick={() => { setImportarXml(true); setXmlTexto('') }} className="btn-secondary">
              <FileText size={15} /> Importar XML
            </button>
            <button onClick={() => setExportar(true)} className="btn-secondary"
              title="Baixar os XMLs do período num ZIP (pro contador)">
              <Download size={15} /> XMLs do período
            </button>
            <button onClick={novaNota} className="btn-primary"><Plus size={15} /> Nova nota</button>
          </div>
        </div>

        {/* Mercadoria que saiu e não voltou: o ICMS está suspenso e o prazo corre.
            Vencendo sem retorno, o imposto vira devido com multa. */}
        {remessas && remessas.total > 0 && (
          <div className={`card card-body ${remessas.vencidas ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className={remessas.vencidas ? 'text-rose-600' : 'text-amber-600'} />
              <span className="font-semibold text-sm">
                {remessas.total} remessa(s) em aberto
                {remessas.vencidas > 0 && (
                  <span className="text-rose-700"> · {remessas.vencidas} com prazo vencido</span>
                )}
              </span>
            </div>
            <div className="space-y-1">
              {remessas.remessas.slice(0, 6).map((r: any) => (
                <div key={r.id} className="flex items-center justify-between gap-3 text-sm py-1 border-t border-black/5 first:border-0">
                  <div className="min-w-0">
                    <span className="font-medium">NF {r.numero || '—'}</span>
                    <span className="text-ink-600"> · {r.destinatario || '—'}</span>
                    <span className="text-ink-500 text-xs"> · {r.natureza_operacao}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs font-medium ${r.vencida ? 'text-rose-700' : 'text-amber-800'}`}>
                      {r.dias_fora != null ? `${r.dias_fora} dias fora` : '—'}
                      {r.dias_restantes != null && (
                        r.vencida
                          ? ` · vencida há ${Math.abs(r.dias_restantes)}d`
                          : ` · restam ${r.dias_restantes}d`
                      )}
                    </span>
                    <button onClick={() => gerarRetorno.mutate(r.id)} disabled={gerarRetorno.isPending}
                      className="text-xs px-2.5 py-1 rounded border border-ink-300 bg-white hover:bg-ink-50 font-medium">
                      Gerar retorno
                    </button>
                  </div>
                </div>
              ))}
              {remessas.remessas.length > 6 && (
                <div className="text-xs text-ink-500 pt-1">
                  + {remessas.remessas.length - 6} outra(s).
                </div>
              )}
            </div>
          </div>
        )}

        {exportar && (
          <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="px-5 py-4 border-b">
                <h3 className="font-semibold text-lg">Baixar XMLs do período</h3>
                <p className="text-xs text-ink-500 mt-0.5">
                  ZIP com os XMLs autorizados + uma planilha de conferência.
                  Canceladas vão numa subpasta.
                </p>
              </div>
              <div className="p-5 grid grid-cols-2 gap-3">
                <div>
                  <label className="label">De</label>
                  <input type="date" className="input" value={expDe} onChange={e => setExpDe(e.target.value)} />
                </div>
                <div>
                  <label className="label">Até</label>
                  <input type="date" className="input" value={expAte} onChange={e => setExpAte(e.target.value)} />
                </div>
              </div>
              <div className="px-5 py-4 border-t flex justify-end gap-2">
                <button onClick={() => setExportar(false)} className="btn-secondary">Voltar</button>
                <button
                  onClick={() => {
                    baixarArquivo(
                      `/nota-avulsa/exportar-xmls?de=${expDe}&ate=${expAte}`,
                      `xmls-nota-avulsa-${expDe}-${expAte}.zip`)
                    setExportar(false)
                  }}
                  className="btn-primary">
                  <Download size={15} /> Baixar ZIP
                </button>
              </div>
            </div>
          </div>
        )}

        {importarXml && (
          <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
              <div className="px-5 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Importar XML de NF-e</h3>
                  <p className="text-xs text-ink-500">
                    Pra devolver uma compra cuja nota não está no sistema. O sistema
                    detecta sozinho se é devolução (nota recebida) ou cópia (nota sua).
                  </p>
                </div>
                <button onClick={() => setImportarXml(false)} className="text-ink-400 hover:text-ink-700">
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <input type="file" accept=".xml,text/xml,application/xml"
                  className="block w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg
                             file:border-0 file:bg-ink-100 file:text-ink-700 file:text-sm"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]
                    if (f) setXmlTexto(await f.text())
                  }} />
                <div className="text-[11px] text-ink-400 text-center">ou cole o conteúdo abaixo</div>
                <textarea className="input font-mono text-[11px]" rows={8}
                  placeholder="<?xml version=&quot;1.0&quot;?><nfeProc ...>"
                  value={xmlTexto} onChange={e => setXmlTexto(e.target.value)} />
              </div>
              <div className="px-5 py-4 border-t flex justify-end gap-2">
                <button onClick={() => { setImportarXml(false); setXmlTexto('') }} className="btn-secondary">
                  Voltar
                </button>
                <button onClick={() => importarDoXml.mutate()}
                  disabled={!xmlTexto.trim() || importarDoXml.isPending}
                  className="btn-primary disabled:opacity-50">
                  {importarDoXml.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
                  Importar
                </button>
              </div>
            </div>
          </div>
        )}

        {importarNF && (
          <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
              <div className="px-5 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Devolução de compra</h3>
                  <p className="text-xs text-ink-500">
                    Escolha a NF de entrada — chave, fornecedor e itens vêm prontos.
                  </p>
                </div>
                <button onClick={() => setImportarNF(false)} className="text-ink-400 hover:text-ink-700">
                  <X size={18} />
                </button>
              </div>
              <div className="px-5 py-3 border-b">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input className="input pl-9" autoFocus placeholder="Nº da nota, chave ou fornecedor…"
                    value={buscaNF} onChange={e => setBuscaNF(e.target.value)} />
                </div>
              </div>
              <div className="flex-1 overflow-auto">
                {nfEntradas.length === 0 && (
                  <p className="text-center py-8 text-sm text-ink-500">
                    Nenhuma NF de entrada confirmada encontrada.
                  </p>
                )}
                {nfEntradas.map((nf: any) => (
                  <button key={nf.id} type="button" disabled={importar.isPending}
                    onClick={() => importar.mutate(nf.id)}
                    className="w-full px-5 py-3 text-left border-b hover:bg-brand-50 flex justify-between gap-3 disabled:opacity-50">
                    <div className="min-w-0">
                      <div className="font-medium text-sm">
                        NF {nf.numero_nf || '—'}/{nf.serie || '—'}
                        <span className="text-ink-500 font-normal"> · {nf.fornecedor_razao || 'Sem fornecedor'}</span>
                      </div>
                      <div className="text-[11px] text-ink-400 font-mono truncate">
                        {nf.chave_acesso || 'sem chave de acesso'}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-medium text-sm">{formatMoney(Number(nf.valor_total || 0))}</div>
                      <div className="text-[11px] text-ink-400">
                        {nf.data_entrada ? new Date(nf.data_entrada).toLocaleDateString('pt-BR') : ''}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Nº</th><th>Status</th><th>Natureza</th><th>Destinatário</th>
                <th>Tipo</th><th className="text-right">Valor</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="text-center py-8 text-ink-400">Carregando...</td></tr>}
              {!isLoading && notas.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-ink-500">
                  Nenhuma nota avulsa. Clique em "Nova nota" pra começar.
                </td></tr>
              )}
              {notas.map((n: any) => {
                const cfg = STATUS_CFG[n.status] || STATUS_CFG.rascunho
                return (
                  <tr key={n.id} className="hover:bg-brand-50/30 cursor-pointer" onClick={() => irParaNota(n.id)}>
                    <td className="font-mono text-xs">{n.numero || '—'}</td>
                    <td><span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>{cfg.label}</span></td>
                    <td className="text-sm">{n.natureza_operacao || '—'}</td>
                    <td className="text-sm">{n.destinatario_razao_social || '—'}</td>
                    <td className="text-xs text-ink-500">{n.tipo_operacao === '0' ? 'Entrada' : 'Saída'}</td>
                    <td className="text-right font-medium">{formatMoney(Number(n.valor_total || 0))}</td>
                    <td className="text-right">
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); duplicar.mutate(n.id) }}
                          disabled={duplicar.isPending}
                          title="Duplicar como novo rascunho"
                          className="text-ink-400 hover:text-brand-600 disabled:opacity-40">
                          <Copy size={14} />
                        </button>
                        {['rascunho', 'rejeitada'].includes(n.status) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (window.confirm(`Excluir a nota #${n.id}?`)) excluir.mutate(n.id)
                            }}
                            className="text-ink-400 hover:text-rose-600"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // ============================================================
  // FORMULÁRIO
  // ============================================================
  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => {
          abertaRef.current = null
          setModo('lista')
          qc.invalidateQueries({ queryKey: ['notas-avulsas'] })
          if (idUrl) navigate('/fiscal/nota-avulsa')
        }} className="btn-secondary"><ArrowLeft size={15} /> Voltar</button>
        <div className="flex items-center gap-2">
          {notaAtual && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${(STATUS_CFG[notaAtual.status] || STATUS_CFG.rascunho).cls}`}>
              {(STATUS_CFG[notaAtual.status] || STATUS_CFG.rascunho).label}
              {notaAtual.numero ? ` · nº ${notaAtual.numero}` : ''}
            </span>
          )}
          {editavel && (
            <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="btn-secondary">
              {salvar.isPending ? <Loader2 size={15} className="animate-spin" /> : null} Salvar
            </button>
          )}
          {notaId && (
            <button onClick={() => verPreview.mutate()} disabled={verPreview.isPending} className="btn-secondary">
              <Eye size={15} /> Preview XML
            </button>
          )}
          {notaId && (
            <button onClick={() => duplicar.mutate(notaId)} disabled={duplicar.isPending}
              title="Criar um rascunho novo com este mesmo conteúdo" className="btn-secondary">
              {duplicar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Copy size={15} />} Duplicar
            </button>
          )}
          {notaId && editavel && (
            <button
              onClick={() => {
                if (window.confirm('Emitir esta nota na SEFAZ? Isso consome um número fiscal e não dá pra desfazer (só cancelar dentro de 24h).')) {
                  emitir.mutate()
                }
              }}
              disabled={emitir.isPending || validacao?.pode_emitir === false}
              title={validacao?.pode_emitir === false ? 'Corrija as pendências abaixo antes de emitir' : ''}
              className="btn-primary disabled:opacity-50">
              {emitir.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Emitir
            </button>
          )}
          {/* Visualizar antes de emitir: o endpoint do DANFE já monta o XML de
              preview quando a nota não foi autorizada — não transmite nem
              consome número. Sai com marca d'água pra ninguém confundir com
              documento válido. */}
          {notaId && notaAtual?.status !== 'autorizada' && (
            <button onClick={() => baixarArquivo(
              `/nota-avulsa/${notaId}/danfe`, `rascunho-${notaAtual?.numero || notaId}.pdf`, true)}
              className="btn-secondary" title="Ver como a nota vai sair, sem emitir">
              <FileText size={15} /> Visualizar nota
            </button>
          )}
          {notaAtual?.status === 'autorizada' && (
            <>
              {/* DANFE abre em aba nova — é dali que se imprime (Ctrl+P) */}
              <button onClick={() => baixarArquivo(
                `/nota-avulsa/${notaId}/danfe`, `danfe-${notaAtual?.numero || notaId}.pdf`, true)}
                className="btn-secondary" title="Abrir o DANFE para visualizar ou imprimir">
                <FileText size={15} /> DANFE / Imprimir
              </button>
              <button onClick={() => baixarArquivo(
                `/nota-avulsa/${notaId}/xml`, `${notaAtual?.chave_acesso || 'nota-' + notaId}.xml`)}
                className="btn-secondary">
                <Download size={15} /> XML
              </button>
              <button
                onClick={() => {
                  setFoneZap(form.destinatario_telefone || '')
                  setEnviarZap(true)
                }}
                className="btn-secondary" title="Enviar DANFE e XML pro destinatário">
                <Send size={15} /> Enviar no zap
              </button>
              <button onClick={() => { setEvento('cce'); setTextoEvento('') }} className="btn-secondary">
                <Pencil size={15} /> Carta de correção
              </button>
              <button onClick={() => { setEvento('cancelar'); setTextoEvento('') }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-rose-300
                           text-rose-700 hover:bg-rose-50 text-sm font-semibold">
                <Ban size={15} /> Cancelar nota
              </button>
            </>
          )}
        </div>
      </div>

      {validacao && editavel && (validacao.bloqueadores?.length > 0 || validacao.avisos?.length > 0) && (
        <div className="space-y-2">
          {validacao.bloqueadores?.length > 0 && (
            <div className="card card-body bg-rose-50 border-rose-200">
              <div className="flex items-center gap-2 font-semibold text-rose-900 mb-1.5">
                <AlertTriangle size={16} /> {validacao.bloqueadores.length} pendência(s) impedem a emissão
              </div>
              <ul className="text-sm text-rose-900 space-y-0.5">
                {validacao.bloqueadores.map((b: string, i: number) => <li key={i}>• {b}</li>)}
              </ul>
            </div>
          )}
          {validacao.avisos?.length > 0 && (
            <div className="card card-body bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2 font-semibold text-amber-900 mb-1.5">
                <AlertTriangle size={16} /> Avisos (não impedem a emissão)
              </div>
              <ul className="text-sm text-amber-900 space-y-0.5">
                {validacao.avisos.map((a: string, i: number) => <li key={i}>• {a}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {validacao?.pode_emitir && editavel && (
        <div className="card card-body bg-emerald-50 border-emerald-200 flex items-center gap-2 text-sm text-emerald-900">
          <CheckCircle2 size={16} /> Nota pronta para emissão.
        </div>
      )}

      {notaAtual?.status === 'pendente_consulta' && (
        <div className="card card-body bg-amber-50 border-amber-200 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <b>A SEFAZ não respondeu a tempo.</b> A nota pode ter sido autorizada mesmo assim —
              por isso o número <u>não</u> foi liberado e a nota está travada.
              <div className="text-xs mt-1">
                Consulte pela chave antes de qualquer coisa. Reemitir sem consultar pode gerar
                duas notas com o mesmo número.
              </div>
            </div>
          </div>
          <button onClick={() => consultar.mutate()} disabled={consultar.isPending}
            className="btn-primary w-fit">
            {consultar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
            Consultar na SEFAZ
          </button>
        </div>
      )}

      {notaAtual?.status === 'rejeitada' && notaAtual?.motivo_status && (
        <div className="card card-body bg-rose-50 border-rose-200 flex items-start gap-2">
          <AlertTriangle size={16} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="text-sm text-rose-900">
            <b>SEFAZ rejeitou ({notaAtual.codigo_status}):</b> {notaAtual.motivo_status}
            <div className="text-xs mt-1 text-rose-700">
              Corrija o que apontou acima e emita de novo — o número foi devolvido, não furou a sequência.
            </div>
          </div>
        </div>
      )}

      {/* Em tela larga, Operação e Destinatário ficam lado a lado — antes o formulário
          ficava preso num max-w-6xl e metade da tela sobrava vazia. */}
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 items-start">
      {/* ===== OPERAÇÃO ===== */}
      <div className="card card-body space-y-3">
        <h3 className="font-semibold text-ink-900">Operação</h3>

        <div>
          <label className="label">O que é esta nota?</label>
          <select className="input" value={form.natureza_operacao_codigo || ''} disabled={!editavel}
            onChange={e => aplicarNatureza(e.target.value)}>
            <option value="">Selecione…</option>
            {/* CFOP na frente: o pessoal escolhe pelo código, não pelo nome. Mostra
                o par intra/inter porque qual dos dois vale só se sabe depois de
                preencher a UF do destinatário — e o rótulo mudar sozinho confunde.
                Com o select aberto, digitar "5102" pula direto pra opção. */}
            {operacoes.map(n => {
              // Sem par cadastrado, o useMemo faz intra e inter caírem no mesmo
              // código (`|| c.codigo`) — o Set evita o rótulo "5102 / 5102".
              const cfops = [...new Set([n.cfop_intra, n.cfop_inter].filter(Boolean))].join(' / ')
              return (
                <option key={n.chave} value={n.chave}>
                  {cfops ? `${cfops} — ${n.label}` : n.label}
                </option>
              )
            })}
          </select>
          {naturezaAtual && (
            <div className="mt-1.5 text-xs text-ink-600 bg-ink-50 rounded px-2.5 py-1.5">
              {naturezaAtual.dica}
              {cfopSugerido && (
                <> <b className="ml-1">Vai usar o CFOP {cfopSugerido}</b>
                  <span className="text-ink-400">
                    {' '}({intraestadual ? 'dentro do estado' : `${ufEmitente} → ${form.destinatario_uf}`})
                  </span>
                </>
              )}
            </div>
          )}
          {!cfopCadastrado && (
            <div className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
              O CFOP <b>{cfopSugerido}</b> não está cadastrado em Fiscal → CFOP. A nota emite
              assim mesmo, mas vale cadastrar pra ele aparecer na lista.
            </div>
          )}
          {operacoes.length === 0 && (
            <div className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
              Nenhuma operação configurada. Vá em <b>Fiscal → CFOP</b>, cadastre os CFOPs que a
              loja usa e preencha a <b>natureza</b> de cada um — eles aparecem aqui automaticamente.
            </div>
          )}
          <p className="text-[11px] text-ink-400 mt-1">
            As opções vêm de Fiscal → CFOP. Preenche sentido, tipo, CFOP e os efeitos —
            tudo continua editável abaixo.
          </p>
        </div>

        {/* Importar de uma venda: evita redigitar cliente e itens de um pedido já fechado. */}
        {editavel && (
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div>
              <label className="label">Código da venda</label>
              <input className="input w-40" value={codVenda} inputMode="numeric"
                placeholder="nº do pedido" disabled={importando}
                onChange={e => setCodVenda(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); importarVenda() } }} />
            </div>
            <button type="button" className="btn-secondary" disabled={importando || !codVenda}
              onClick={importarVenda}>
              {importando ? 'Importando…' : 'Importar venda'}
            </button>
            <span className="text-xs text-ink-500 pb-2">
              Puxa cliente, itens e frete. Natureza e CFOP continuam sendo sua escolha.
            </span>
          </div>
        )}

        {impAvisos.length > 0 && (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>Confira antes de emitir:</strong>
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              {impAvisos.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}

        {/* Emitente sempre à vista: nota no CNPJ errado é cancelamento, não correção. */}
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-3 py-2 mb-3 text-sm border
          ${filialEmit ? 'bg-brand-50 border-brand-200 text-ink-800'
                       : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
          <span className="text-xs uppercase tracking-wide text-ink-500">Emitindo por</span>
          <strong>{filialEmit
            ? (filialEmit.nome_fantasia || filialEmit.razao_social)
            : 'Nenhuma filial selecionada'}</strong>
          {cnpjEmitente && <span className="font-mono">CNPJ {cnpjEmitente}</span>}
          {ufEmitente && <span className="text-ink-500">{ufEmitente}</span>}
          {filialEmit && !cnpjEmitente && (
            <span className="text-red-600">⚠ filial sem CNPJ cadastrado</span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="label">Filial emitente</label>
            <select className="input" value={form.filial_id} disabled={!editavel}
              onChange={e => set('filial_id', e.target.value ? Number(e.target.value) : '')}>
              <option value="">(minha filial)</option>
              {filiais.map((f: any) => (
                <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Sentido</label>
            <select className="input" value={form.tipo_operacao} disabled={!editavel}
              onChange={e => set('tipo_operacao', e.target.value)}>
              <option value="1">Saída</option>
              <option value="0">Entrada</option>
            </select>
          </div>
          <div>
            <label className="label">Tipo da nota</label>
            <select className="input" value={form.finalidade} disabled={!editavel}
              onChange={e => set('finalidade', e.target.value)}>
              <option value="1">Normal</option>
              <option value="2">Complementar</option>
              <option value="3">Ajuste</option>
              <option value="4">Devolução</option>
            </select>
          </div>
          <div>
            <label className="label">CFOP</label>
            <select className="input" value={form.cfop_principal} disabled={!editavel}
              onChange={e => {
                set('cfop_principal', e.target.value)
                // aplica o CFOP nos itens que ainda não têm — evita nota rejeitada por item sem CFOP
                setItens(prev => prev.map(i => (i.cfop ? i : { ...i, cfop: e.target.value })))
              }}>
              <option value="">Selecione…</option>
              {/* CFOP sugerido pela natureza mas ainda não cadastrado: entra na lista
                  mesmo assim, senão o campo ficaria vazio e a nota sairia sem CFOP. */}
              {form.cfop_principal && !cfops.some((c: any) => c.codigo === form.cfop_principal) && (
                <option value={form.cfop_principal}>{form.cfop_principal} — (não cadastrado)</option>
              )}
              {cfopsFiltrados.map((c: any) => (
                <option key={c.id} value={c.codigo}>{c.codigo} — {c.descricao}</option>
              ))}
            </select>
            {cfopsFiltrados.length === 0 && (
              <p className="text-[11px] text-amber-700 mt-1">
                Nenhum CFOP de {ehEntrada ? 'entrada' : 'saída'} cadastrado — cadastre em Fiscal → CFOP.
              </p>
            )}
          </div>

          {/* Regra de imposto: só aparece depois do CFOP, porque é a ele que a
              regra está vinculada. Aplicada, vira a BASE de todos os itens —
              inclusive dos componentes de kit, que o backend rederiva. */}
          {form.cfop_principal && (
            <div>
              <label className="label">Regra de imposto</label>
              <select className="input" disabled={!editavel}
                value={form.regra_imposto_id ?? ''}
                onChange={e => escolherRegra(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— tributação de cada produto —</option>
                {(regras as any[]).map(r => (
                  <option key={r.id} value={r.id}>
                    {r.nome}{r.qtd_campos ? ` (${r.qtd_campos} campos)` : ' (vazia)'}
                  </option>
                ))}
              </select>
              {regrasCarregando && (
                <p className="text-[11px] text-ink-400 mt-1">Buscando regras…</p>
              )}
              {!regrasCarregando && regras.length === 0 && (
                <p className="text-[11px] text-ink-500 mt-1">
                  Nenhuma regra para o CFOP {form.cfop_principal} — cadastre em
                  Cadastros → Regra de Imposto.
                </p>
              )}
              {regraAtual && editavel && (
                <button type="button" onClick={reaplicarRegra}
                  className="text-[11px] text-brand-700 hover:underline mt-1">
                  Reaplicar a todos os itens (descarta edições manuais)
                </button>
              )}
            </div>
          )}
          </div>


        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="label">Natureza da operação (sai na nota)</label>
            <input className="input" value={form.natureza_operacao} disabled={!editavel}
              placeholder="Ex.: Transferência de mercadoria"
              onChange={e => set('natureza_operacao', e.target.value)} />
          </div>
          <div>
            <label className="label">Presença</label>
            <select className="input" value={form.presenca_comprador} disabled={!editavel}
              onChange={e => set('presenca_comprador', e.target.value)}>
              <option value="9">Não se aplica</option>
              <option value="1">Presencial</option>
              <option value="2">Internet</option>
            </select>
          </div>
        </div>

        {/* Transferência: escolher a loja destino preenche o destinatário sozinho
            e faz a pré-entrada aparecer lá quando a nota autorizar. */}
        <div>
          <label className="label">Loja de destino (transferência)</label>
          <select className="input" value={form.filial_destino_id ?? ''} disabled={!editavel}
            onChange={e => set('filial_destino_id', e.target.value ? Number(e.target.value) : null)}>
            <option value="">— não é transferência entre lojas —</option>
            {filiais.filter((f: any) => f.id !== form.filial_id).map((f: any) => (
              <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
            ))}
          </select>
          {form.filial_destino_id && (
            <p className="text-[11px] text-sky-700 mt-1">
              O destinatário é preenchido com os dados desta loja ao salvar. Quando a nota
              autorizar, uma <b>pré-entrada</b> é criada lá para conferência.
            </p>
          )}
        </div>

        {form.nf_entrada_id && (
          <div className="text-xs bg-sky-50 border border-sky-200 rounded px-2.5 py-1.5 text-sky-900">
            Devolução vinculada à NF de entrada #{form.nf_entrada_id} — as quantidades não
            podem passar do que foi recebido.
          </div>
        )}

        {exigeRef && (
          <div>
            <label className="label">Chave da NF original (44 dígitos) — obrigatória nesta finalidade</label>
            <input className="input font-mono text-xs" value={form.chave_nfe_referenciada} disabled={!editavel}
              maxLength={44} placeholder="Sem ela a SEFAZ rejeita"
              onChange={e => set('chave_nfe_referenciada', e.target.value.replace(/\D/g, ''))} />
          </div>
        )}

        <div className="flex flex-wrap gap-5 items-center pt-1 border-t border-ink-100">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.gera_estoque} disabled={!editavel}
              onChange={e => set('gera_estoque', e.target.checked)} />
            Movimenta estoque
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.gera_financeiro} disabled={!editavel}
              onChange={e => set('gera_financeiro', e.target.checked)} />
            Gera financeiro
          </label>
          {form.gera_financeiro && (
            <>
              <div>
                <label className="label">Forma de pagamento</label>
                <select className="input w-48" value={form.forma_pagamento} disabled={!editavel}
                  onChange={e => set('forma_pagamento', e.target.value)}>
                  <option value="">Selecione…</option>
                  {formasPagamento.map((f: any) => (
                    <option key={f.id ?? f.forma} value={f.forma}>{f.label || f.forma}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">1º vencimento</label>
                <input type="date" className="input w-40" disabled={!editavel}
                  value={form.financeiro_primeiro_vencimento}
                  onChange={e => set('financeiro_primeiro_vencimento', e.target.value)} />
              </div>
              <div>
                <label className="label">Parcelas</label>
                <input type="number" min={1} max={60} className="input w-24" disabled={!editavel}
                  value={form.financeiro_parcelas}
                  onChange={e => set('financeiro_parcelas', Number(e.target.value) || 1)} />
                {Number(form.financeiro_parcelas) > 1 && (
                  <p className="text-[11px] text-ink-500 mt-0.5">de 30 em 30 dias</p>
                )}
              </div>
            </>
          )}
          <span className="text-[11px] text-ink-500">
            Desmarcados: a nota é só documento fiscal, não mexe em estoque nem cria cobrança.
          </span>
        </div>
      </div>

      {/* ===== DESTINATÁRIO ===== */}
      <div className="card card-body space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-ink-900">Destinatário</h3>
          <select className="input w-40" value={form.destinatario_tipo} disabled={!editavel}
            onChange={e => set('destinatario_tipo', e.target.value)}>
            <option value="cliente">Cliente</option>
            <option value="fornecedor">Fornecedor</option>
          </select>
        </div>

        {editavel && (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-9" placeholder="Buscar no cadastro (ou preencha manualmente abaixo)…"
              value={buscaPessoa} onChange={e => setBuscaPessoa(e.target.value)} />
            {pessoas.length > 0 && buscaPessoa.trim().length >= 2 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                {pessoas.map((p: any) => (
                  <button key={p.id} type="button"
                    onClick={() => {
                      setForm(f => ({
                        ...f, destinatario_id: p.id,
                        destinatario_razao_social: p.nome || '',
                        destinatario_cnpj_cpf: p.documento || '',
                        destinatario_ie: p.ie || '',
                        destinatario_indicador_ie: (p.ie && p.ie.toUpperCase() !== 'ISENTO') ? '1' : '9',
                        destinatario_endereco: p.endereco || '', destinatario_numero: p.numero || '',
                        destinatario_complemento: p.complemento || '', destinatario_bairro: p.bairro || '',
                        destinatario_municipio: p.cidade || '', destinatario_codigo_ibge: p.codigo_ibge_cidade || '',
                        destinatario_uf: p.uf || '', destinatario_cep: p.cep || '',
                        destinatario_telefone: p.telefone || '', destinatario_email: p.email || '',
                      }))
                      setBuscaPessoa('')
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-brand-50 border-b last:border-0 text-sm">
                    {p.nome} <span className="text-ink-400 text-xs">{p.documento}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="label">Razão social / Nome</label>
            <input className="input" value={form.destinatario_razao_social} disabled={!editavel}
              onChange={e => set('destinatario_razao_social', e.target.value)} />
          </div>
          <div>
            <label className="label">CNPJ / CPF</label>
            <input
              className={`input ${docValido === false ? 'border-rose-400 focus:border-rose-500' : ''}`}
              value={form.destinatario_cnpj_cpf} disabled={!editavel}
              onChange={e => set('destinatario_cnpj_cpf', e.target.value)} />
            {docValido === false && (
              <p className="text-[11px] text-rose-600 mt-0.5">Dígito verificador não confere.</p>
            )}
            {docValido === true && (
              <p className="text-[11px] text-emerald-600 mt-0.5">Documento válido.</p>
            )}
          </div>
          <div>
            <label className="label">Inscrição Estadual</label>
            <input className="input" value={form.destinatario_ie} disabled={!editavel}
              onChange={e => set('destinatario_ie', e.target.value)} />
          </div>
          <div>
            <label className="label">CEP</label>
            <div className="flex gap-1">
              <input className="input" value={form.destinatario_cep} disabled={!editavel}
                placeholder="00000-000"
                onChange={e => set('destinatario_cep', e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarCep() } }} />
              <button type="button" onClick={buscarCep} disabled={!editavel}
                title="Buscar endereço pelo CEP"
                className="px-2.5 rounded-lg border border-ink-200 hover:bg-ink-50 disabled:opacity-40">
                <Search size={15} />
              </button>
            </div>
          </div>
          {([
            ['destinatario_endereco', 'Endereço', 'md:col-span-2'],
            ['destinatario_numero', 'Número', ''],
            ['destinatario_bairro', 'Bairro', ''],
            ['destinatario_municipio', 'Município', ''],
            ['destinatario_codigo_ibge', 'Cód. IBGE', ''],
            ['destinatario_uf', 'UF', ''],
          ] as [string, string, string][]).map(([campo, label, cls]) => (
            <div key={campo} className={cls}>
              <label className="label">{label}</label>
              <input className="input" value={(form as any)[campo] || ''} disabled={!editavel}
                onChange={e => set(campo, e.target.value)} />
            </div>
          ))}
          <div>
            <label className="label">Indicador IE</label>
            <select className="input" value={form.destinatario_indicador_ie} disabled={!editavel}
              onChange={e => set('destinatario_indicador_ie', e.target.value)}>
              <option value="1">Contribuinte</option>
              <option value="2">Isento</option>
              <option value="9">Não contribuinte</option>
            </select>
          </div>
        </div>
      </div>

      </div>

      {/* ===== ITENS ===== */}
      <div className="card card-body space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-ink-900">Itens</h3>
          {editavel && (
            <button onClick={addLivre} className="btn-secondary text-xs">
              <Plus size={13} /> Item sem cadastro
            </button>
          )}
        </div>

        {editavel && (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-9" placeholder="Buscar produto no catálogo…"
              value={buscaProd} onFocus={() => setAbrirProd(true)}
              onChange={e => { setBuscaProd(e.target.value); setAbrirProd(true) }} />
            {abrirProd && produtos.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                {produtos.map((p: any) => (
                  <button key={p.id} type="button" onClick={() => addProdutoComKit(p)}
                    className="w-full px-3 py-2 text-left hover:bg-brand-50 border-b last:border-0 text-sm flex justify-between">
                    <span><span className="font-mono text-xs text-ink-400 mr-2">{p.codigo}</span>{p.descricao}</span>
                    <span className="text-ink-500">{formatMoney(Number(p.preco_venda || 0))}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="table-wrap">
          <table className="table text-xs">
            <thead>
              <tr>
                <th>Código</th><th>Descrição</th><th>NCM</th><th>CFOP</th><th>Un</th>
                <th className="text-right">Qtd</th><th className="text-right">Vl. Unit</th>
                <th className="text-right">Total</th><th></th>
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-ink-500">Nenhum item.</td></tr>
              )}
              {itens.map((it, idx) => {
                // Componente de kit fechado: não renderiza (o índice real é
                // preservado porque o map percorre a lista inteira).
                const ehFilho = !!it.kit_pai_produto_id
                if (ehFilho && !kitsAbertos.has(it.kit_pai_produto_id!)) return null
                const ehKit = !!it.eh_kit_pai
                const aberto = ehKit && !!it.produto_id && kitsAbertos.has(it.produto_id)
                const qtdFilhos = ehKit
                  ? itens.filter(x => x.kit_pai_produto_id === it.produto_id).length
                  : 0
                // Numa filial loja o XML leva os componentes; na indústria, o kit.
                // O lado que não vai fica esmaecido pra ninguém preencher à toa.
                const inerte = (ehKit && !kitVaiInteiro) || (ehFilho && kitVaiInteiro)

                return (
                <tr key={idx} className={[
                  // Nada de bg-brand-50/bg-ink-50 aqui: são tons claros da paleta e,
                  // no tema escuro, viram um bloco branco que apaga o texto.
                  // Barra lateral + peso da fonte marcam a hierarquia sem mexer no fundo.
                  ehKit ? 'font-medium border-l-2 border-brand-500' : '',
                  ehFilho ? 'border-l-2 border-ink-400/40' : '',
                ].join(' ')}>
                  <td>
                    <div className="flex items-center gap-1">
                      {ehKit ? (
                        <button type="button"
                          onClick={() => setKitsAbertos(prev => {
                            const n = new Set(prev)
                            if (n.has(it.produto_id!)) n.delete(it.produto_id!)
                            else n.add(it.produto_id!)
                            return n
                          })}
                          title={aberto ? 'Recolher componentes' : `Ver os ${qtdFilhos} componentes`}
                          className="text-ink-500 hover:text-brand-600 shrink-0">
                          {aberto ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </button>
                      ) : (
                        <span className={ehFilho ? 'w-[15px] shrink-0 border-l border-ink-300 h-4 ml-1.5' : 'w-[15px] shrink-0'} />
                      )}
                      <input className="input !py-1 w-24" value={it.codigo}
                        disabled={!editavel || ehFilho}
                        onChange={e => setItem(idx, 'codigo', e.target.value)} />
                    </div>
                  </td>
                  <td>
                    <input className="input !py-1 min-w-[200px]" value={it.descricao}
                      disabled={!editavel || ehFilho}
                      onChange={e => setItem(idx, 'descricao', e.target.value)} />
                    {ehKit && (
                      <span className="text-[10px] text-brand-700 ml-1">
                        kit · {qtdFilhos} componente(s)
                        {!kitVaiInteiro && ' · a nota sai pelas peças'}
                      </span>
                    )}
                  </td>
                  <td><input className="input !py-1 w-24" value={it.ncm} disabled={!editavel}
                    onChange={e => setItem(idx, 'ncm', e.target.value)} /></td>
                  <td><input className="input !py-1 w-20" value={it.cfop} disabled={!editavel}
                    onChange={e => setItem(idx, 'cfop', e.target.value)} /></td>
                  <td><input className="input !py-1 w-16" value={it.unidade} disabled={!editavel}
                    onChange={e => setItem(idx, 'unidade', e.target.value)} /></td>
                  <td><input type="number" className="input !py-1 w-20 text-right" value={it.quantidade}
                    disabled={!editavel || ehFilho}
                    title={ehFilho ? 'Vem da quantidade do kit — edite na linha da bike' : ''}
                    onChange={e => (ehKit
                      ? setItemKitPai(idx, 'quantidade', Number(e.target.value))
                      : setItem(idx, 'quantidade', Number(e.target.value)))} /></td>
                  <td><input type="number" step="0.01" className="input !py-1 w-28 text-right" value={it.valor_unitario}
                    disabled={!editavel || ehFilho}
                    title={ehFilho ? 'Rateado a partir do valor do kit' : ''}
                    onChange={e => (ehKit
                      ? setItemKitPai(idx, 'valor_unitario', Number(e.target.value))
                      : setItem(idx, 'valor_unitario', Number(e.target.value)))} /></td>
                  <td className="text-right font-medium">
                    {formatMoney(it.quantidade * it.valor_unitario - (it.valor_desconto || 0))}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => setTribIdx(idx)}
                      title={(() => {
                        const n = (it.campos_manuais || []).length
                        const base = inerte
                          ? 'Esta tributação não vai pro XML nesta filial'
                          : 'Tributação do item (CST/CSOSN, redução, ST, PIS/COFINS, IPI)'
                        return n
                          ? `${base}\n\n${n} campo(s) editado(s) na mão: ${(it.campos_manuais || []).join(', ')}.\nA regra de imposto não sobrescreve estes.`
                          : base
                      })()}
                      className={`mr-1.5 relative ${(it.campos_manuais || []).length
                        ? 'text-amber-600 hover:text-amber-700'
                        : 'text-ink-400 hover:text-brand-600'}`}>
                      <Percent size={14} />
                      {/* Ponto âmbar = linha com edição manual. Sem ele, o operador
                          não entende por que a regra "não pegou" neste item. */}
                      {!!(it.campos_manuais || []).length && (
                        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />
                      )}
                    </button>
                    {editavel && !ehFilho && (
                      <button onClick={() => setItens(prev => prev.filter((x, i) => (
                        i !== idx && !(it.eh_kit_pai && x.kit_pai_produto_id === it.produto_id)
                      )))}
                        title={ehKit ? 'Remove a bike e todos os componentes' : 'Remover item'}
                        className="text-ink-400 hover:text-rose-600"><X size={14} /></button>
                    )}
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-3 items-end justify-between">
          <div className="flex gap-3">
            {([['valor_frete', 'Frete'], ['valor_seguro', 'Seguro'], ['valor_outras_despesas', 'Outras desp.']] as [string, string][])
              .map(([campo, label]) => (
                <div key={campo}>
                  <label className="label">{label}</label>
                  <input type="number" step="0.01" className="input w-28" value={(form as any)[campo]}
                    disabled={!editavel} onChange={e => set(campo, e.target.value)} />
                </div>
              ))}
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-500">Total da nota</div>
            <div className="text-2xl font-bold text-brand-700">{formatMoney(totalNota)}</div>
          </div>
        </div>
      </div>

      {/* ===== TRIBUTAÇÃO DO ITEM ===== */}
      {tribIdx !== null && itens[tribIdx] && (
        <ModalTributacao
          item={itens[tribIdx]}
          usaCsosn={usaCsosn}
          editavel={editavel}
          kitVaiInteiro={kitVaiInteiro}
          onFechar={() => setTribIdx(null)}
          onSalvar={(novo) => setItens(prev => prev.map((it, i) => {
            // O modal tem rascunho interno e só devolve no "Pronto" — então o que
            // mudou entre o item antigo e o novo É a edição manual do operador.
            // Comparar com a REGRA não serviria: digitar o mesmo valor que ela
            // define é indistinguível de não ter mexido.
            if (i !== tribIdx) return it
            const mudou = CAMPOS_REGRA.filter(
              c => String((novo as any)[c] ?? '') !== String((it as any)[c] ?? ''))
            return marcarManual(novo, mudou)
          }))}
        />
      )}

      {/* ===== TRANSPORTE ===== */}
      <details className="card card-body" open={!!form.transp_nome || !!form.vol_quantidade}>
        <summary className="font-semibold text-ink-900 cursor-pointer">
          Transporte e volumes
          <span className="font-normal text-xs text-ink-500 ml-2">
            (opcional — importante quando a mercadoria vai pra estrada)
          </span>
        </summary>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="label">Transportadora</label>
              <input className="input" value={form.transp_nome} disabled={!editavel}
                placeholder="Nome / razão social" onChange={e => set('transp_nome', e.target.value)} />
            </div>
            <div>
              <label className="label">CNPJ/CPF</label>
              <input className="input" value={form.transp_cnpj_cpf} disabled={!editavel}
                onChange={e => set('transp_cnpj_cpf', e.target.value)} />
            </div>
            <div>
              <label className="label">IE</label>
              <input className="input" value={form.transp_ie} disabled={!editavel}
                onChange={e => set('transp_ie', e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Endereço</label>
              <input className="input" value={form.transp_endereco} disabled={!editavel}
                onChange={e => set('transp_endereco', e.target.value)} />
            </div>
            <div>
              <label className="label">Município</label>
              <input className="input" value={form.transp_municipio} disabled={!editavel}
                onChange={e => set('transp_municipio', e.target.value)} />
            </div>
            <div>
              <label className="label">UF</label>
              <input className="input" maxLength={2} value={form.transp_uf} disabled={!editavel}
                onChange={e => set('transp_uf', e.target.value.toUpperCase())} />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="label">Placa</label>
              <input className="input" maxLength={8} value={form.transp_placa} disabled={!editavel}
                placeholder="ABC1D23" onChange={e => set('transp_placa', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className="label">UF da placa</label>
              <input className="input" maxLength={2} value={form.transp_uf_placa} disabled={!editavel}
                onChange={e => set('transp_uf_placa', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className="label">RNTC</label>
              <input className="input" value={form.transp_rntc} disabled={!editavel}
                title="Registro Nacional de Transportador de Carga (ANTT)"
                onChange={e => set('transp_rntc', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="label">Qtd. volumes</label>
              <input type="number" min={0} className="input" value={form.vol_quantidade} disabled={!editavel}
                onChange={e => set('vol_quantidade', e.target.value)} />
            </div>
            <div>
              <label className="label">Espécie</label>
              <input className="input" value={form.vol_especie} disabled={!editavel}
                placeholder="Caixa, Fardo, Bicicleta…" onChange={e => set('vol_especie', e.target.value)} />
            </div>
            <div>
              <label className="label">Marca</label>
              <input className="input" value={form.vol_marca} disabled={!editavel}
                onChange={e => set('vol_marca', e.target.value)} />
            </div>
            <div>
              <label className="label">Peso líquido (kg)</label>
              <input type="number" step="0.001" className="input" value={form.vol_peso_liquido}
                disabled={!editavel} onChange={e => set('vol_peso_liquido', e.target.value)} />
            </div>
            <div>
              <label className="label">Peso bruto (kg)</label>
              <input type="number" step="0.001" className="input" value={form.vol_peso_bruto}
                disabled={!editavel} onChange={e => set('vol_peso_bruto', e.target.value)} />
            </div>
          </div>
        </div>
      </details>

      {/* ===== INFO ADICIONAIS ===== */}
      <div className="card card-body grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Informações complementares (sai na DANFE)</label>
          <textarea className="input" rows={2} value={form.informacoes_adicionais} disabled={!editavel}
            onChange={e => set('informacoes_adicionais', e.target.value)} />
        </div>
        <div>
          <label className="label">Informações ao Fisco</label>
          <textarea className="input" rows={2} value={form.informacoes_fisco} disabled={!editavel}
            onChange={e => set('informacoes_fisco', e.target.value)} />
        </div>
      </div>

      {enviarZap && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b">
              <h3 className="font-semibold text-lg">Enviar nota pelo WhatsApp</h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Vai a DANFE em PDF e o XML. Sai pelo número da loja que emitiu.
              </p>
            </div>
            <div className="p-5">
              <label className="label">Telefone do destinatário</label>
              <input className="input" autoFocus value={foneZap} placeholder="85 99999-9999"
                onChange={e => setFoneZap(e.target.value)} />
              {!form.destinatario_telefone && (
                <p className="text-[11px] text-amber-700 mt-1">
                  O destinatário não tem telefone cadastrado — confira o número antes de enviar.
                </p>
              )}
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <button onClick={() => setEnviarZap(false)} className="btn-secondary">Voltar</button>
              <button onClick={() => enviarWhats.mutate()}
                disabled={enviarWhats.isPending || foneZap.replace(/\D/g, '').length < 10}
                className="btn-primary disabled:opacity-50">
                {enviarWhats.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EVENTOS (cancelamento / CC-e) ===== */}
      {evento && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-5 py-4 border-b">
              <h3 className="font-semibold text-lg">
                {evento === 'cancelar' ? 'Cancelar nota na SEFAZ' : 'Carta de correção'}
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">
                {evento === 'cancelar'
                  ? 'Só dá pra cancelar em até 24h da autorização. A nota deixa de valer.'
                  : 'Corrige apenas erro de TEXTO. Não pode mudar valores, destinatário, datas ou produtos.'}
              </p>
            </div>
            <div className="p-5 space-y-2">
              <label className="label">
                {evento === 'cancelar' ? 'Justificativa' : 'Texto da correção'}
              </label>
              <textarea className="input" rows={4} autoFocus value={textoEvento}
                placeholder={evento === 'cancelar'
                  ? 'Ex.: Nota emitida com dados incorretos do destinatario'
                  : 'Ex.: Onde se le AV BRASIL, leia-se AV BRASIL 1200'}
                onChange={e => setTextoEvento(e.target.value)} />
              <div className="flex justify-between text-[11px]">
                <span className={textoEvento.trim().length < 15 ? 'text-rose-600' : 'text-ink-400'}>
                  Mínimo 15 caracteres (a SEFAZ exige)
                </span>
                <span className="text-ink-400">
                  {textoEvento.trim().length}/{evento === 'cancelar' ? 255 : 1000}
                </span>
              </div>

              {evento === 'cancelar' && (
                <div className="pt-2 border-t">
                  <label className="label">Senha de autorização</label>
                  <input type="password" className="input" value={senhaEvento} autoComplete="new-password"
                    placeholder="Senha de admin, gerente ou financeiro"
                    onChange={e => setSenhaEvento(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.preventDefault() }} />
                  <p className="text-[11px] text-ink-500 mt-1">
                    Se o seu usuário já tem cargo, use a sua própria senha.
                  </p>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <button onClick={() => { setEvento(null); setTextoEvento(''); setSenhaEvento('') }}
                className="btn-secondary">
                Voltar
              </button>
              <button
                disabled={
                  textoEvento.trim().length < 15
                  || textoEvento.trim().length > (evento === 'cancelar' ? 255 : 1000)
                  || (evento === 'cancelar' && !senhaEvento)
                  || cancelarNota.isPending || emitirCCe.isPending
                }
                onClick={() => {
                  if (evento === 'cancelar') {
                    if (window.confirm('Cancelar esta nota na SEFAZ? Não dá pra desfazer.')) {
                      cancelarNota.mutate()
                    }
                  } else {
                    emitirCCe.mutate()
                  }
                }}
                className={evento === 'cancelar'
                  ? 'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50'
                  : 'btn-primary disabled:opacity-50'}>
                {(cancelarNota.isPending || emitirCCe.isPending)
                  ? <Loader2 size={15} className="animate-spin" /> : null}
                {evento === 'cancelar' ? 'Cancelar nota' : 'Enviar correção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {eventos && eventos.total > 0 && (
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-2">Eventos da nota</h3>
          <div className="space-y-1.5">
            {eventos.eventos.map((e: any) => (
              <div key={e.id} className="flex items-start justify-between gap-3 text-sm py-1.5 border-t border-ink-100 first:border-0">
                <div className="min-w-0">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold mr-2 ${
                    e.tipo === 'cancelamento' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>
                    {e.tipo === 'cancelamento' ? 'CANCELAMENTO' : `CC-e ${e.sequencia || ''}`}
                  </span>
                  <span className="text-ink-700">{e.texto}</span>
                  {e.xMotivo && <div className="text-[11px] text-ink-400 mt-0.5">{e.cStat} · {e.xMotivo}</div>}
                </div>
                <div className="text-[11px] text-ink-400 shrink-0 text-right">
                  {e.created_at ? new Date(e.created_at).toLocaleString('pt-BR') : ''}
                  {e.protocolo && <div className="font-mono">{e.protocolo}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== PREVIEW ===== */}
      {preview && (
        <div className="card card-body space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-ink-900 flex items-center gap-2">
              {preview.alertas?.length
                ? <AlertTriangle size={16} className="text-amber-600" />
                : <CheckCircle2 size={16} className="text-emerald-600" />}
              Preview do XML ({preview.ambiente_descricao})
            </h3>
            <button onClick={() => setPreview(null)} className="text-ink-400 hover:text-ink-700"><X size={16} /></button>
          </div>
          {preview.alertas?.length > 0 && (
            <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5">
              {preview.alertas.map((a: string, i: number) => <li key={i}>• {a}</li>)}
            </ul>
          )}

          {/* Totais Gerais — lidos do XML gerado, exatamente como vão pra nota */}
          {preview.totais && Object.keys(preview.totais).length > 0 && (
            <div className="border border-ink-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-ink-700 uppercase mb-2">Totais gerais</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
                {([
                  ['Base ICMS', 'vBC'], ['Valor ICMS', 'vICMS'],
                  ['Base ICMS ST', 'vBCST'], ['Valor ICMS ST', 'vST'],
                  ['FCP', 'vFCP'], ['FCP ST', 'vFCPST'],
                  ['Valor IPI', 'vIPI'], ['PIS', 'vPIS'],
                  ['COFINS', 'vCOFINS'], ['Total produtos', 'vProd'],
                  ['Frete', 'vFrete'], ['Seguro', 'vSeg'],
                  ['Outras despesas', 'vOutro'], ['Desconto', 'vDesc'],
                  ['Trib. aproximados', 'vTotTrib'],
                ] as [string, string][]).map(([label, tag]) => (
                  <div key={tag}>
                    <div className="text-[11px] text-ink-500">{label}</div>
                    <div className="font-mono text-ink-900">
                      {formatMoney(Number(preview.totais[tag] || 0))}
                    </div>
                  </div>
                ))}
                <div className="col-span-2 md:col-span-4 border-t pt-2 mt-1 flex justify-between items-center">
                  <span className="font-semibold text-ink-900">Valor total da nota</span>
                  <span className="px-2.5 py-1 rounded-md bg-brand-600 text-white font-bold text-lg">
                    {formatMoney(Number(preview.totais.vNF || 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}